// i18n 6amico.com — lingue ufficiali UE + fallback IT

/**
 * Lingua di default del network (Home, UI).
 * Priorità assoluta: italiano. Browser / locale ambigui → sempre "it".
 * Solo la bandierina (scelta esplicita) attiva en/fr/de/….
 */
export const DEFAULT_LANG = "it";

/** Lookup stile i18next: t("hero.title") + accesso oggetto t.hero.title */
export function makeT(dict) {
  const resolve = (path, vars) => {
    if (typeof path !== "string" || !path.trim()) return "";
    const parts = path.split(".");
    let cur = dict;
    for (const p of parts) {
      if (cur == null || typeof cur !== "object") return "";
      cur = cur[p];
    }
    let out = typeof cur === "string" ? cur : cur == null ? "" : String(cur);
    if (vars && typeof vars === "object" && out) {
      for (const [k, v] of Object.entries(vars)) {
        out = out.split(`{${k}}`).join(String(v ?? ""));
      }
    }
    return out;
  };
  const fn = (path, vars) => resolve(path, vars);
  return new Proxy(fn, {
    get(target, prop, receiver) {
      if (typeof prop === "symbol") return Reflect.get(target, prop, receiver);
      // Evita che React/thenable tratti t come Promise
      if (prop === "then") return undefined;
      // Chiavi del dizionario hanno priorità su proprietà Function (name, length, …)
      if (dict != null && Object.prototype.hasOwnProperty.call(dict, prop)) {
        return dict[prop];
      }
      if (prop === "length" || prop === "name" || prop === "prototype" || prop === "apply" || prop === "call" || prop === "bind") {
        return Reflect.get(target, prop, receiver);
      }
      return dict?.[prop];
    },
  });
}

/** Emoji bandiera leggibili (header / switcher compatto) */
export const LANG_EMOJI = {
  it: "🇮🇹",
  en: "🇬🇧",
  fr: "🇫🇷",
  de: "🇩🇪",
  es: "🇪🇸",
  pt: "🇵🇹",
  nl: "🇳🇱",
  pl: "🇵🇱",
  hr: "🇭🇷",
  el: "🇬🇷",
  ro: "🇷🇴",
  cs: "🇨🇿",
  sk: "🇸🇰",
  hu: "🇭🇺",
  bg: "🇧🇬",
  da: "🇩🇰",
  sv: "🇸🇪",
  fi: "🇫🇮",
  et: "🇪🇪",
  lv: "🇱🇻",
  lt: "🇱🇹",
  sl: "🇸🇮",
  mt: "🇲🇹",
  ga: "🇮🇪",
};

/** Menu header: lingue principali (niente lista infinita) */
export const PRIMARY_LANGUAGES = [
  { code: "it", label: "Italiano", emoji: LANG_EMOJI.it },
  { code: "en", label: "English", emoji: LANG_EMOJI.en },
  { code: "fr", label: "Français", emoji: LANG_EMOJI.fr },
  { code: "de", label: "Deutsch", emoji: LANG_EMOJI.de },
  { code: "es", label: "Español", emoji: LANG_EMOJI.es },
  { code: "pt", label: "Português", emoji: LANG_EMOJI.pt },
];

export const LANGUAGES = [
  ...PRIMARY_LANGUAGES.map(({ code, label }) => ({ code, label, flag: code.toUpperCase(), emoji: LANG_EMOJI[code] })),
  { code: "nl", label: "Nederlands", flag: "NL", emoji: LANG_EMOJI.nl },
  { code: "pl", label: "Polski", flag: "PL", emoji: LANG_EMOJI.pl },
  { code: "hr", label: "Hrvatski", flag: "HR", emoji: LANG_EMOJI.hr },
  { code: "el", label: "Ελληνικά", flag: "GR", emoji: LANG_EMOJI.el },
  { code: "ro", label: "Română", flag: "RO", emoji: LANG_EMOJI.ro },
  { code: "cs", label: "Čeština", flag: "CZ", emoji: LANG_EMOJI.cs },
  { code: "sk", label: "Slovenčina", flag: "SK", emoji: LANG_EMOJI.sk },
  { code: "hu", label: "Magyar", flag: "HU", emoji: LANG_EMOJI.hu },
  { code: "bg", label: "Български", flag: "BG", emoji: LANG_EMOJI.bg },
  { code: "da", label: "Dansk", flag: "DK", emoji: LANG_EMOJI.da },
  { code: "sv", label: "Svenska", flag: "SE", emoji: LANG_EMOJI.sv },
  { code: "fi", label: "Suomi", flag: "FI", emoji: LANG_EMOJI.fi },
  { code: "et", label: "Eesti", flag: "EE", emoji: LANG_EMOJI.et },
  { code: "lv", label: "Latviešu", flag: "LV", emoji: LANG_EMOJI.lv },
  { code: "lt", label: "Lietuvių", flag: "LT", emoji: LANG_EMOJI.lt },
  { code: "sl", label: "Slovenščina", flag: "SI", emoji: LANG_EMOJI.sl },
  { code: "mt", label: "Malti", flag: "MT", emoji: LANG_EMOJI.mt },
  { code: "ga", label: "Gaeilge", flag: "IE", emoji: LANG_EMOJI.ga },
];

const SUPPORTED = new Set(LANGUAGES.map((l) => l.code));

export function isSupportedLang(code) {
  return SUPPORTED.has((code || "it").split("-")[0].toLowerCase());
}

function deepMerge(base, patch) {
  if (!patch) return base;
  const out = { ...base };
  for (const key of Object.keys(patch)) {
    const pv = patch[key];
    const bv = base[key];
    if (pv && typeof pv === "object" && !Array.isArray(pv) && bv && typeof bv === "object" && !Array.isArray(bv)) {
      out[key] = deepMerge(bv, pv);
    } else {
      out[key] = pv;
    }
  }
  return out;
}

const GDPR_IT = {
  title: "Privacy e dati personali",
  lead: "Consenso granulare, portabilità e cancellazione (Reg. UE 2016/679).",
  policy_link: "Informativa completa →",
  consents_title: "Consenso al trattamento",
  essential: "Essenziali (servizio, sicurezza)",
  functional: "Funzionali (preferenze, lingua)",
  analytics: "Statistiche anonime",
  marketing: "Comunicazioni promozionali",
  third_party_maps: "Mappe e servizi di terze parti",
  community_publication: "Pubblicazione su Piazze Social",
  download: "Scarica i miei dati (JSON)",
  downloaded: "Download avviato",
  saved: "Preferenze salvate",
  erase_title: "Cancellazione account",
  erase_lead: "Eliminazione definitiva di profilo, post e punti (Art. 17).",
  erase_btn: "Elimina account",
  erase_confirm: "Conferma cancellazione",
  confirm_placeholder: "Digita ELIMINA per confermare",
  password: "Password",
  cancel: "Annulla",
  error: "Operazione non riuscita",
  language: "Lingua dell'interfaccia",
};

const NARRATION_IT = {
  listen: "Ascolta",
  pause: "Pausa",
  preparing: "Preparo la voce…",
  home_title: "Ascolta il testo storico",
  home_hint: "Presentazione in voce calda — stesso sistema dei monumenti.",
  home_storia_hint: "Legge il testo storico del borgo qui sotto (come nelle schede comune), non le foto del carosello.",
  vocal_label: "Narrazione vocale",
  fallback_notice: "Uso la voce del dispositivo: il servizio vocale del server non ha risposto.",
  server_notice: "Voce narrativa femminile 6amico",
  unavailable: "Narrazione non disponibile.",
  interrupted: "Narrazione interrotta. Riprova tra poco.",
};

const NARRATION_EN = {
  listen: "Listen",
  pause: "Pause",
  preparing: "Preparing voice…",
  home_title: "Listen to the home page",
  home_hint: "Warm voice presentation — same system as monument pages.",
  home_storia_hint: "Reads the mission and historical platform text below — not the photo carousel.",
  vocal_label: "Voice narration",
  fallback_notice: "Using your device voice: the server audio service did not respond.",
  server_notice: "6amico female narrative voice",
  unavailable: "Narration not available.",
  interrupted: "Narration stopped. Try again shortly.",
};

const GDPR_EN = {
  title: "Privacy & personal data",
  lead: "Granular consent, portability and erasure (EU GDPR).",
  policy_link: "Full privacy notice →",
  consents_title: "Processing consent",
  essential: "Essential (service, security)",
  functional: "Functional (preferences, language)",
  analytics: "Anonymous analytics",
  marketing: "Promotional communications",
  third_party_maps: "Third-party maps & services",
  community_publication: "Social Squares publication",
  download: "Download my data (JSON)",
  downloaded: "Download started",
  saved: "Preferences saved",
  erase_title: "Delete account",
  erase_lead: "Permanent deletion of profile, posts and points (Art. 17).",
  erase_btn: "Delete account",
  erase_confirm: "Confirm deletion",
  confirm_placeholder: "Type DELETE to confirm",
  cancel: "Cancel",
  error: "Operation failed",
  language: "Interface language",
};

export const translations = {
  it: {
    narration: NARRATION_IT,
    nav: {
      home: "Home",
      villages: "Borghi",
      qr: "Guide QR",
      piazze: "Piazza Social",
      merchants: "Vetrina",
      twinnings: "Gemellaggi",
      explore: "Esplora Borghi",
      events: "Eventi & Sagre",
      network: "Network",
      world: "Mondo",
    },
    hero: {
      payoff: "ITALIAN TRAVEL EXPERIENCE",
      service_line: "MAPPING | HISTORY | HOSPITALITY | AIR | SEA | MOBILE",
      global_tagline: "YOUR DIGITAL GUIDE TO ITALY'S HIDDEN HERITAGE",
      kicker: "Dal 2013 · Guide intelligenti per i borghi",
      title: "Ogni pietra ha una storia. Scoprila.",
      subtitle: "Dai borghi d'Italia alle città del mondo, la memoria dei luoghi è a portata di clic.",
      sub: "Dai borghi d'Italia alle città del mondo, la memoria dei luoghi è a portata di clic.",
      cta_primary: "Scopri i Borghi",
      cta_explore: "Esplora",
      cta_secondary: "Scansiona un QR",
      qr_manual_toggle: "Hai un codice QR? Inseriscilo",
    },
    sections: {
      mission: "La nostra missione",
      mission_lead: "Restituiamo memoria e storie dei borghi a chi li vive e a chi li visita.",
      villages: "I Borghi",
      villages_sub: "Territori da esplorare, a partire dall'Abruzzo",
      qr: "Guide intelligenti",
      qr_sub: "Scansiona un QR sul territorio e apri la guida del luogo",
      piazze: "La piazza digitale",
      piazze_sub: "Foto, eventi e racconti dalla community dei borghi",
      merchants: "Turismo e territorio",
      merchants_sub: "Prodotti tipici e botteghe locali in vetrina",
      twinnings: "Gemellaggi",
      twinnings_sub: "Borghi italiani collegati alle città amiche nel mondo",
      chat: "Parla con 6amico",
      chat_sub: "Chiedi consigli su borghi, storie e prodotti tipici",
    },
    pages: {
      piazze: {
        kicker: "Network del Merito · Italia",
        title: "Piazza Social",
        lead: "Il feed condiviso di tutti i borghi del Network. Ogni post è un ricordo, una notizia o una segnalazione fatta dai cittadini. Reagisci con il Campanile, commenta, condividi una cartolina con la tua famiglia.",
        feed_aria: "Feed Piazza Social",
      },
      vetrina: {
        kicker: "MARKET · TERRITORIO",
        search_placeholder: "Cerca un comune… es. Campli, Castiglione",
        featured: "Borghi in evidenza",
        merchants_all: "Commercianti del network",
        merchants_for: "Vetrina · {name}",
        empty: "Nessun risultato per questo comune. Prova un altro borgo dalla ricerca.",
        filter_label: "Filtra",
        remove_filter: "Rimuovi",
      },
      eventi: {
        kicker: "CALENDARIO · TERRITORIO",
        title: "Eventi & Sagre",
        lead: "Solo date future, senza doppioni. Eventi pubblicati in archivio da Pro Loco e Comuni del network — si aggiorna quando le istituzioni caricano nuove date.",
        suggest: "Suggerisci un evento",
        loading: "Caricamento...",
        empty_title: "Nessun evento in arrivo",
        empty_lead: "Le Pro Loco e i Comuni stanno per pubblicare i prossimi eventi.",
        cat_all: "Tutti",
        cat_sagra: "Sagre",
        cat_festa: "Feste",
        cat_manifestazione: "Manifestazioni",
        cat_concerto: "Concerti",
        cat_mostra: "Mostre",
        cat_religioso: "Religiosi",
        cat_sportivo: "Sportivi",
      },
    },
    ui: {
      read_more: "Approfondisci",
      discover: "Scopri",
      visit: "Visita",
      by: "a cura di",
      year: "Anno",
      code: "Codice QR",
      scan_cta: "Inserisci codice QR (es. CAMPLI-001)",
      scan_button: "Visualizza Guida",
      contact: "Contatta",
      specialty: "Specialità",
      featured: "In evidenza",
      since: "Gemellato dal",
      country: "Paese",
      post: "Post",
      event: "Evento",
      news: "Notizia",
      new_post: "Pubblica qualcosa…",
      publish: "Pubblica",
      your_name: "Il tuo nome",
      choose_village: "Scegli borgo",
      ask_placeholder: "Chiedi allo Storico…",
      send: "Invia",
      open_chat: "Apri chat",
      close: "Chiudi",
      view_all: "Vedi tutto",
      show_translation: "Visualizza traduzione",
      footer_tagline: "6amico è un social network italiano dedicato alla scoperta e alla valorizzazione dei borghi, delle frazioni, degli eventi e dei prodotti tipici dei territori.",
      back_home: "Torna alla Home",
      landmarks: "Luoghi iconici",
      province: "Provincia",
      founded: "Fondato",
      population: "Abitanti",
      loading: "Caricamento…",
      not_found: "Contenuto non trovato",
      frazioni: "Frazioni",
      monuments: "Monumenti",
      qr_download: "Scarica QR Code",
      qr_print: "Stampa il QR per installarlo sul territorio",
      admin: "Amministrazione",
      admin_login: "Accedi al pannello",
      password: "Password",
      logout: "Esci",
      save: "Salva", saving: "Salvo…", saved: "Salvato",
      ai_fill: "Genera con AI", generating: "Generazione…",
      add_monument: "Aggiungi monumento", delete: "Elimina",
      video: "Video", paste_youtube: "Incolla URL YouTube/Vimeo",
      gallery: "Galleria", add_image_url: "URL immagine",
      add: "Aggiungi", edit: "Modifica",
      no_frazioni: "Nessuna frazione disponibile", explore_frazione: "Esplora",
      language: "Lingua",
      tabs: {
        storia: "Storia", storia_md: "Storia & info",
        frazioni: "Frazioni", frazioni_world: "Comuni",
        percorsi: "Percorsi", percorsi_md: "Percorsi e sentieri",
        mangiare: "Mangiare", mangiare_md: "Dove mangiare",
        dormire: "Dormire", dormire_md: "Dove dormire",
        prodotti: "Prodotti", prodotti_md: "Prodotti del comune", prodotti_world: "Specialità", prodotti_world_md: "Prodotti & specialità locali",
        luoghi: "Luoghi", luoghi_md: "Luoghi & cultura",
        proloco: "Pro Loco", proloco_md: "Pro Loco & eventi", proloco_world: "Eventi", proloco_world_md: "Eventi & istituzioni",
        altro: "Altro", movida: "Movida", movida_md: "Movida e notte", sub: "Sub", sub_md: "Sub e diving", barche: "Barche",
        luoghi_cosa_vedere: "Cosa vedere", luoghi_chiese: "Chiese", luoghi_musei: "Musei", luoghi_natura: "Natura",
        culto: "Culto", culto_title: "Luoghi di culto", monumenti: "Monumenti · Cosa vedere",
        chiama: "Chiama", sito: "Sito", indicazioni: "Indicazioni",
        link_istituzionale: "Link istituzionale", link_istituzionale_cultura: "Link istituzionale · cultura", video_istituzionale: "Video · link istituzionale",
        utility: "Utility", sismica: "Sismica", meteo: "Meteo",
        installa_app: "Installa App", installa_title: "Installa 6amico su iPhone",
        installa_intro: "Per avere 6amico a schermo intero come una vera applicazione sul tuo iPhone:",
        installa_s1: "Tocca l'icona Condividi in basso al centro in Safari (il quadrato con la freccia in su).",
        installa_s2: "Scorri il menù e tocca \"Aggiungi alla schermata Home\".",
        installa_s3: "Conferma con \"Aggiungi\" in alto a destra.",
        installa_done: "Ho capito",
        installa_hint_desktop: "Per installare l'app, clicca sull'icona con lo schermo o il '+' nella barra degli indirizzi del tuo browser.",
      },
    },
    footer: {
      blurb: "6amico valorizza i borghi italiani: mappe, storie, eventi e prodotti tipici in una sola community.",
      trust: "Privacy · GDPR",
      nav_title: "Navigazione",
      community_title: "Community",
      discover_title: "Scopri",
      welcome_tour: "Tour di benvenuto",
      how_it_works: "Come funziona",
      manifesto: "Manifesto",
      generation: "Generazione 6AMICO",
      census: "Censisci un luogo",
      request_access: "Richiedi un accesso",
      map: "Mappa dei borghi",
      comuni: "Comuni amici",
      vetrina: "Vetrina tipica",
      institutional: "Area istituzionale",
      institutional_lead: "Per comuni, Pro Loco e partner",
      open: "Apri",
      close: "Chiudi",
    },
    gdpr: GDPR_IT,
  },
  en: {
    narration: NARRATION_EN,
    nav: {
      home: "Home",
      villages: "Villages",
      qr: "QR Guides",
      piazze: "Social Squares",
      merchants: "Marketplace",
      twinnings: "Twinnings",
      explore: "Explore Villages",
      events: "Events & Festivals",
      network: "Network",
      world: "World",
    },
    hero: {
      payoff: "ITALIAN TRAVEL EXPERIENCE",
      service_line: "MAPPING | HISTORY | HOSPITALITY | AIR | SEA | MOBILE",
      global_tagline: "YOUR DIGITAL GUIDE TO ITALY'S HIDDEN HERITAGE",
      kicker: "2013–2026 · YOUR DIGITAL CICERONE ACROSS ITALY",
      title: "Every stone has a story. Discover it.",
      subtitle: "From Italy's villages to the cities of the world, the memory of places is one click away.",
      sub: "From Italy's villages to the cities of the world, the memory of places is one click away.",
      cta_primary: "Discover the Villages",
      cta_explore: "Explore",
      cta_secondary: "Scan a QR",
      qr_manual_toggle: "Have a code (e.g. CAMPLI-001)? Enter it here",
    },
    sections: {
      mission: "Our Mission",
      mission_lead: "We've retrieved thousands of historical texts and images from our archives to return a unique cultural heritage to citizens and travelers.",
      villages: "The Villages",
      villages_sub: "Four gateways into Abruzzo's memory",
      qr: "Smart Tourism",
      qr_sub: "Scan a QR Code on-site to unlock guides curated by historians",
      piazze: "Social Squares",
      piazze_sub: "Geolocated feed: photos, events and stories from the villages",
      merchants: "Local Economy",
      merchants_sub: "A showcase for merchants and producers — the soul of the territory",
      twinnings: "Sister cities",
      twinnings_sub: "Italian villages linked to partner cities worldwide",
      chat: "Talk to 6amico",
      chat_sub: "Ask anything about Abruzzo's history",
    },
    pages: {
      piazze: {
        kicker: "Merit Network · Italy",
        title: "Social Square",
        lead: "The shared feed of every village in the Network. Each post is a memory, a news item or a tip from citizens. React with the Bell Tower, comment, share a postcard with your family.",
        feed_aria: "Social Square feed",
      },
      vetrina: {
        kicker: "MARKET · TERRITORY",
        search_placeholder: "Search a town… e.g. Campli, Castiglione",
        featured: "Featured villages",
        merchants_all: "Network merchants",
        merchants_for: "Showcase · {name}",
        empty: "No results for this town. Try another village from search.",
        filter_label: "Filter",
        remove_filter: "Remove",
      },
      eventi: {
        kicker: "CALENDAR · TERRITORY",
        title: "Events & Festivals",
        lead: "Upcoming dates only, duplicates filtered. Events published in the archive by Pro Loco and network municipalities — updated when institutions add new dates.",
        suggest: "Suggest an event",
        loading: "Loading...",
        empty_title: "No upcoming events",
        empty_lead: "Pro Loco and municipalities will publish the next events soon.",
        cat_all: "All",
        cat_sagra: "Food festivals",
        cat_festa: "Feasts",
        cat_manifestazione: "Gatherings",
        cat_concerto: "Concerts",
        cat_mostra: "Exhibitions",
        cat_religioso: "Religious",
        cat_sportivo: "Sports",
      },
    },
    ui: {
      read_more: "Read more", discover: "Discover", visit: "Visit", by: "curated by", year: "Year",
      code: "QR Code", scan_cta: "Enter QR code (e.g. CAMPLI-001)", scan_button: "Open Guide",
      contact: "Contact", specialty: "Specialty", featured: "Featured", since: "Twinned since", country: "Country",
      post: "Post", event: "Event", news: "News", new_post: "Share something…", publish: "Publish",
      your_name: "Your name", choose_village: "Choose village", ask_placeholder: "Ask the Historian…",
      send: "Send", open_chat: "Open chat", close: "Close", view_all: "View all",
      footer_tagline: "The Territorial Social Network · 2013–2026", back_home: "Back home",
      landmarks: "Iconic places", province: "Province", founded: "Founded", population: "Population",
      loading: "Loading…", not_found: "Content not found",
      frazioni: "Hamlets", monuments: "Monuments", qr_download: "Download QR Code",
      qr_print: "Print the QR to install on the territory",
      admin: "Admin", admin_login: "Sign in", password: "Password", logout: "Logout",
      save: "Save", saving: "Saving…", saved: "Saved", ai_fill: "Generate with AI", generating: "Generating…",
      add_monument: "Add monument", delete: "Delete", video: "Video", paste_youtube: "Paste YouTube/Vimeo URL",
      gallery: "Gallery", add_image_url: "Image URL", add: "Add", edit: "Edit",
      no_frazioni: "No hamlets available", explore_frazione: "Explore",
            language: "Language",
      tabs: {
        storia: "History", storia_md: "History & info",
        frazioni: "Hamlets", frazioni_world: "Communes",
        percorsi: "Routes", percorsi_md: "Routes & trails",
        mangiare: "Eat", mangiare_md: "Where to eat",
        dormire: "Sleep", dormire_md: "Where to stay",
        prodotti: "Products", prodotti_md: "Local products", prodotti_world: "Specialities", prodotti_world_md: "Local products & specialities",
        luoghi: "Places", luoghi_md: "Places & culture",
        proloco: "Pro Loco", proloco_md: "Pro Loco & events", proloco_world: "Events", proloco_world_md: "Events & institutions",
        altro: "More", movida: "Nightlife", movida_md: "Nightlife", sub: "Diving", sub_md: "Diving", barche: "Boats",
        luoghi_cosa_vedere: "What to see", luoghi_chiese: "Churches", luoghi_musei: "Museums", luoghi_natura: "Nature",
        culto: "Worship", culto_title: "Places of worship", monumenti: "Monuments · What to see",
        chiama: "Call", sito: "Website", indicazioni: "Directions",
        link_istituzionale: "Institutional link", link_istituzionale_cultura: "Institutional link · culture", video_istituzionale: "Video · institutional link",
        utility: "Utility", sismica: "Seismic", meteo: "Weather",
        installa_app: "Install App", installa_title: "Install 6amico on iPhone",
        installa_intro: "To use 6amico full-screen like a real app on your iPhone:",
        installa_s1: "Tap the Share icon at the bottom centre of Safari (the square with an arrow pointing up).",
        installa_s2: "Scroll the menu and tap \"Add to Home Screen\".",
        installa_s3: "Confirm with \"Add\" at the top right.",
        installa_done: "Got it",
        installa_hint_desktop: "To install the app, click the install icon or the '+' in your browser's address bar.",
      },
    },
    footer: {
      blurb: "6amico showcases Italian villages: maps, stories, events and local products in one community.",
      trust: "Privacy · GDPR",
      nav_title: "Navigation",
      community_title: "Community",
      discover_title: "Discover",
      welcome_tour: "Welcome tour",
      how_it_works: "How it works",
      manifesto: "Manifesto",
      generation: "Generation 6AMICO",
      census: "Map a place",
      request_access: "Request access",
      map: "Village map",
      comuni: "Partner towns",
      vetrina: "Local showcase",
      institutional: "Institutional area",
      institutional_lead: "For municipalities, Pro Loco and partners",
      open: "Open",
      close: "Close",
    },
    gdpr: GDPR_EN,
  },
  fr: {
    narration: {
      ...NARRATION_EN,
      listen: "Écouter",
      pause: "Pause",
      preparing: "Préparation de la voix…",
      home_title: "Écouter la page d'accueil",
      home_hint: "Présentation vocale — même système que les monuments.",
      vocal_label: "Narration vocale",
    },
    nav: {
      home: "Accueil",
      villages: "Villages",
      qr: "Guides QR",
      piazze: "Places Sociales",
      merchants: "Vitrine",
      twinnings: "Jumelages",
      explore: "Explorer les villages",
      events: "Événements & Fêtes",
      network: "Réseau",
      world: "Monde",
    },
    hero: {
      payoff: "ITALIAN TRAVEL EXPERIENCE",
      service_line: "MAPPING | HISTORY | HOSPITALITY | AIR | SEA | MOBILE",
      global_tagline: "YOUR DIGITAL GUIDE TO ITALY'S HIDDEN HERITAGE",
      kicker: "2013–2026 · VOTRE CICÉRONE NUMÉRIQUE EN ITALIE",
      title: "Chaque pierre a une histoire. Découvrez-la.",
      subtitle: "Des villages d'Italie aux villes du monde, la mémoire des lieux est à portée de clic.",
      sub: "Des villages d'Italie aux villes du monde, la mémoire des lieux est à portée de clic.",
      cta_primary: "Découvrez les Villages",
      cta_explore: "Explorer",
      cta_secondary: "Scanner un QR",
      qr_manual_toggle: "Vous avez un code (ex. CAMPLI-001) ? Saisissez-le ici",
    },
    sections: {
      mission: "Notre Mission",
      mission_lead: "Nous avons récupéré des milliers de textes et images historiques pour restituer un patrimoine culturel unique.",
      villages: "Les Villages", villages_sub: "Quatre portes sur la mémoire des Abruzzes",
      qr: "Tourisme Intelligent", qr_sub: "Scannez un QR Code sur le territoire pour débloquer les guides",
      piazze: "Places Sociales", piazze_sub: "Flux géolocalisé : photos, événements et récits",
      merchants: "Économie Locale", merchants_sub: "Une vitrine pour les commerçants et producteurs",
      twinnings: "Jumelage Mondial", twinnings_sub: "Chaque village, une petite capitale du monde",
      chat: "Parler avec 6amico", chat_sub: "L'IA qui relie les villages, préserve la mémoire et suggère des produits locaux",
    },
    pages: {
      piazze: {
        kicker: "Réseau du Mérite · Italie",
        title: "Place Sociale",
        lead: "Le fil partagé de tous les villages du Réseau. Chaque publication est un souvenir, une nouvelle ou un signalement des citoyens.",
        feed_aria: "Fil Place Sociale",
      },
      vetrina: {
        kicker: "MARCHÉ · TERRITOIRE",
        search_placeholder: "Chercher une commune… ex. Campli, Castiglione",
        featured: "Villages à la une",
        merchants_all: "Commerçants du réseau",
        merchants_for: "Vitrine · {name}",
        empty: "Aucun résultat pour cette commune. Essayez un autre village.",
        filter_label: "Filtrer",
        remove_filter: "Retirer",
      },
      eventi: {
        kicker: "CALENDRIER · TERRITOIRE",
        title: "Événements & Fêtes",
        lead: "Fêtes, festivals, manifestations et concerts publiés par les Pro Loco et les communes du réseau.",
        suggest: "Suggérer un événement",
        loading: "Chargement...",
        empty_title: "Aucun événement à venir",
        empty_lead: "Les Pro Loco et les communes publieront bientôt les prochains événements.",
        cat_all: "Tous",
        cat_sagra: "Fêtes gastronomiques",
        cat_festa: "Fêtes",
        cat_manifestazione: "Manifestations",
        cat_concerto: "Concerts",
        cat_mostra: "Expositions",
        cat_religioso: "Religieux",
        cat_sportivo: "Sportifs",
      },
    },
    ui: {
      read_more: "En savoir plus", discover: "Découvrir", visit: "Visiter", by: "par", year: "Année",
      code: "Code QR", scan_cta: "Entrez le code QR (ex. CAMPLI-001)", scan_button: "Ouvrir le guide",
      contact: "Contacter", specialty: "Spécialité", featured: "Mis en avant", since: "Jumelé depuis", country: "Pays",
      post: "Publication", event: "Événement", news: "Actualité", new_post: "Partagez quelque chose…", publish: "Publier",
      your_name: "Votre nom", choose_village: "Choisir un village", ask_placeholder: "Posez une question…",
      send: "Envoyer", open_chat: "Ouvrir le chat", close: "Fermer", view_all: "Tout voir",
      footer_tagline: "Le Réseau Social du Territoire · 2013–2026", back_home: "Retour à l'accueil",
      landmarks: "Lieux emblématiques", province: "Province", founded: "Fondé", population: "Habitants",
      loading: "Chargement…", not_found: "Contenu introuvable",
      frazioni: "Hameaux", monuments: "Monuments", qr_download: "Télécharger QR Code",
      qr_print: "Imprime le QR pour l'installer sur le territoire",
      admin: "Admin", admin_login: "Se connecter", password: "Mot de passe", logout: "Déconnexion",
      save: "Enregistrer", saving: "Enregistrement…", saved: "Enregistré", ai_fill: "Générer avec IA", generating: "Génération…",
      add_monument: "Ajouter monument", delete: "Supprimer", video: "Vidéo", paste_youtube: "Coller URL YouTube/Vimeo",
      gallery: "Galerie", add_image_url: "URL image", add: "Ajouter", edit: "Modifier",
            no_frazioni: "Aucun hameau disponible", explore_frazione: "Explorer",
      tabs: {
        storia: "Histoire", storia_md: "Histoire & infos",
        frazioni: "Hameaux", frazioni_world: "Communes",
        percorsi: "Itinéraires", percorsi_md: "Itinéraires & sentiers",
        mangiare: "Manger", mangiare_md: "Où manger",
        dormire: "Dormir", dormire_md: "Où dormir",
        prodotti: "Produits", prodotti_md: "Produits du terroir", prodotti_world: "Spécialités", prodotti_world_md: "Produits & spécialités locales",
        luoghi: "Lieux", luoghi_md: "Lieux & culture",
        proloco: "Pro Loco", proloco_md: "Pro Loco & événements", proloco_world: "Événements", proloco_world_md: "Événements & institutions",
        altro: "Autre", movida: "Vie nocturne", movida_md: "Vie nocturne", sub: "Plongée", sub_md: "Plongée", barche: "Bateaux",
        luoghi_cosa_vedere: "À voir", luoghi_chiese: "Églises", luoghi_musei: "Musées", luoghi_natura: "Nature",
        culto: "Culte", culto_title: "Lieux de culte", monumenti: "Monuments · À voir",
        chiama: "Appeler", sito: "Site", indicazioni: "Itinéraire",
        link_istituzionale: "Lien institutionnel", link_istituzionale_cultura: "Lien institutionnel · culture", video_istituzionale: "Vidéo · lien institutionnel",
        utility: "Utilitaire", sismica: "Sismique", meteo: "Météo",
        installa_app: "Installer l'app", installa_title: "Installer 6amico sur iPhone",
        installa_intro: "Pour avoir 6amico en plein écran comme une vraie application sur votre iPhone :",
        installa_s1: "Touchez l'icône Partager en bas au centre de Safari (le carré avec la flèche vers le haut).",
        installa_s2: "Faites défiler le menu et touchez « Sur l'écran d'accueil ».",
        installa_s3: "Confirmez avec « Ajouter » en haut à droite.",
        installa_done: "J'ai compris",
        installa_hint_desktop: "Pour installer l'app, cliquez sur l'icône d'installation ou le « + » dans la barre d'adresse du navigateur.",
      },
    },
    footer: {
      blurb: "6amico valorise les villages italiens : cartes, histoires, événements et produits locaux en une seule communauté.",
      trust: "Confidentialité · RGPD",
      nav_title: "Navigation",
      community_title: "Communauté",
      discover_title: "Découvrir",
      welcome_tour: "Visite de bienvenue",
      how_it_works: "Comment ça marche",
      manifesto: "Manifeste",
      generation: "Génération 6AMICO",
      census: "Recenser un lieu",
      request_access: "Demander un accès",
      map: "Carte des villages",
      comuni: "Communes amies",
      vetrina: "Vitrine locale",
      institutional: "Espace institutionnel",
      institutional_lead: "Pour communes, Pro Loco et partenaires",
      open: "Ouvrir",
      close: "Fermer",
    },
  },
  de: {
    narration: {
      ...NARRATION_EN,
      listen: "Anhören",
      pause: "Pause",
      preparing: "Stimme wird vorbereitet…",
      home_title: "Startseite anhören",
      home_hint: "Warme Sprachpräsentation — wie bei Denkmälern.",
      vocal_label: "Sprachführung",
    },
    nav: {
      home: "Start",
      villages: "Dörfer",
      qr: "QR-Guides",
      piazze: "Soziale Plätze",
      merchants: "Markt",
      twinnings: "Partnerschaften",
      explore: "Dörfer entdecken",
      events: "Events & Feste",
      network: "Netzwerk",
      world: "Welt",
    },
    hero: {
      payoff: "ITALIAN TRAVEL EXPERIENCE",
      service_line: "MAPPING | HISTORY | HOSPITALITY | AIR | SEA | MOBILE",
      global_tagline: "YOUR DIGITAL GUIDE TO ITALY'S HIDDEN HERITAGE",
      kicker: "2013–2026 · IHR DIGITALER CICERONE IN ITALIEN",
      title: "Jeder Stein hat eine Geschichte. Entdecken Sie sie.",
      subtitle: "Von den Dörfern Italiens zu den Städten der Welt – die Erinnerung der Orte ist nur einen Klick entfernt.",
      sub: "Von den Dörfern Italiens zu den Städten der Welt – die Erinnerung der Orte ist nur einen Klick entfernt.",
      cta_primary: "Dörfer entdecken",
      cta_explore: "Entdecken",
      cta_secondary: "QR scannen",
      qr_manual_toggle: "Code auf dem Schild (z. B. CAMPLI-001)? Hier eingeben",
    },
    sections: {
      mission: "Unsere Mission",
      mission_lead: "Wir haben Tausende historische Texte und Bilder aus unseren Archiven geborgen.",
      villages: "Die Dörfer", villages_sub: "Vier Tore in das Gedächtnis der Abruzzen",
      qr: "Intelligenter Tourismus", qr_sub: "Scannen Sie einen QR-Code vor Ort",
      piazze: "Soziale Plätze", piazze_sub: "Geolokalisierter Feed",
      merchants: "Lokale Wirtschaft", merchants_sub: "Eine Vitrine für Händler und Produzenten",
      twinnings: "Weltweite Partnerschaften", twinnings_sub: "Jedes Dorf, eine kleine Welthauptstadt",
      chat: "Mit 6amico sprechen", chat_sub: "Die KI, die Dörfer verbindet, Erinnerungen bewahrt und lokale Produkte empfiehlt",
    },
    pages: {
      piazze: {
        kicker: "Merit-Netzwerk · Italien",
        title: "Sozialer Platz",
        lead: "Der gemeinsame Feed aller Dörfer im Netzwerk. Jeder Beitrag ist eine Erinnerung, eine Nachricht oder ein Hinweis von Bürgern.",
        feed_aria: "Feed Sozialer Platz",
      },
      vetrina: {
        kicker: "MARKT · TERRITORIUM",
        search_placeholder: "Gemeinde suchen… z. B. Campli, Castiglione",
        featured: "Empfohlene Dörfer",
        merchants_all: "Händler im Netzwerk",
        merchants_for: "Schaufenster · {name}",
        empty: "Keine Ergebnisse für diese Gemeinde. Versuchen Sie ein anderes Dorf.",
        filter_label: "Filter",
        remove_filter: "Entfernen",
      },
      eventi: {
        kicker: "KALENDER · TERRITORIUM",
        title: "Events & Feste",
        lead: "Feste, Feiern, Veranstaltungen und Konzerte von Pro Loco und Gemeinden des Netzwerks.",
        suggest: "Event vorschlagen",
        loading: "Lädt...",
        empty_title: "Keine kommenden Events",
        empty_lead: "Pro Loco und Gemeinden veröffentlichen bald die nächsten Events.",
        cat_all: "Alle",
        cat_sagra: "Dorffeste",
        cat_festa: "Feste",
        cat_manifestazione: "Veranstaltungen",
        cat_concerto: "Konzerte",
        cat_mostra: "Ausstellungen",
        cat_religioso: "Religiös",
        cat_sportivo: "Sport",
      },
    },
    ui: {
      read_more: "Mehr lesen", discover: "Entdecken", visit: "Besuchen", by: "kuratiert von", year: "Jahr",
      code: "QR-Code", scan_cta: "QR-Code eingeben (z.B. CAMPLI-001)", scan_button: "Guide öffnen",
      contact: "Kontakt", specialty: "Spezialität", featured: "Empfohlen", since: "Partnerschaft seit", country: "Land",
      post: "Beitrag", event: "Event", news: "Nachricht", new_post: "Etwas teilen…", publish: "Veröffentlichen",
      your_name: "Ihr Name", choose_village: "Dorf wählen", ask_placeholder: "Frage den Historiker…",
      send: "Senden", open_chat: "Chat öffnen", close: "Schließen", view_all: "Alle anzeigen",
      footer_tagline: "Das soziale Netzwerk des Territoriums · 2013–2026", back_home: "Zur Startseite",
      landmarks: "Sehenswürdigkeiten", province: "Provinz", founded: "Gegründet", population: "Einwohner",
      loading: "Lädt…", not_found: "Inhalt nicht gefunden",
      frazioni: "Ortsteile", monuments: "Denkmäler", qr_download: "QR-Code herunterladen",
      qr_print: "Drucken Sie den QR-Code zum Aufstellen vor Ort",
      admin: "Admin", admin_login: "Anmelden", password: "Passwort", logout: "Abmelden",
      save: "Speichern", saving: "Speichere…", saved: "Gespeichert", ai_fill: "Mit KI generieren", generating: "Generiere…",
      add_monument: "Denkmal hinzufügen", delete: "Löschen", video: "Video", paste_youtube: "YouTube/Vimeo-URL einfügen",
      gallery: "Galerie", add_image_url: "Bild-URL", add: "Hinzufügen", edit: "Bearbeiten",
      no_frazioni: "Keine Ortsteile verfügbar", explore_frazione: "Erkunden",
            language: "Sprache",
      tabs: {
        storia: "Geschichte", storia_md: "Geschichte & Infos",
        frazioni: "Ortsteile", frazioni_world: "Gemeinden",
        percorsi: "Wege", percorsi_md: "Wege & Wanderwege",
        mangiare: "Essen", mangiare_md: "Wo essen",
        dormire: "Schlafen", dormire_md: "Wo übernachten",
        prodotti: "Produkte", prodotti_md: "Lokale Produkte", prodotti_world: "Spezialitäten", prodotti_world_md: "Produkte & Spezialitäten",
        luoghi: "Orte", luoghi_md: "Orte & Kultur",
        proloco: "Pro Loco", proloco_md: "Pro Loco & Veranstaltungen", proloco_world: "Veranstaltungen", proloco_world_md: "Veranstaltungen & Institutionen",
        altro: "Mehr", movida: "Nachtleben", movida_md: "Nachtleben", sub: "Tauchen", sub_md: "Tauchen", barche: "Boote",
        luoghi_cosa_vedere: "Sehenswürdigkeiten", luoghi_chiese: "Kirchen", luoghi_musei: "Museen", luoghi_natura: "Natur",
        culto: "Kultus", culto_title: "Kultstätten", monumenti: "Denkmäler · Sehenswürdigkeiten",
        chiama: "Anrufen", sito: "Website", indicazioni: "Anfahrt",
        link_istituzionale: "Institutioneller Link", link_istituzionale_cultura: "Institutioneller Link · Kultur", video_istituzionale: "Video · institutioneller Link",
        utility: "Dienste", sismica: "Seismik", meteo: "Wetter",
        installa_app: "App installieren", installa_title: "6amico auf dem iPhone installieren",
        installa_intro: "Um 6amico im Vollbild wie eine echte App auf Ihrem iPhone zu nutzen:",
        installa_s1: "Tippen Sie in Safari unten mittig auf das Teilen-Symbol (das Quadrat mit dem Pfeil nach oben).",
        installa_s2: "Scrollen Sie im Menü und tippen Sie auf „Zum Home-Bildschirm“.",
        installa_s3: "Bestätigen Sie oben rechts mit „Hinzufügen“.",
        installa_done: "Verstanden",
        installa_hint_desktop: "Zum Installieren klicken Sie auf das Installations-Symbol oder das „+“ in der Adressleiste Ihres Browsers.",
      },
    },
    footer: {
      blurb: "6amico macht italienische Dörfer sichtbar: Karten, Geschichten, Events und lokale Produkte in einer Community.",
      trust: "Datenschutz · DSGVO",
      nav_title: "Navigation",
      community_title: "Community",
      discover_title: "Entdecken",
      welcome_tour: "Willkommens-Tour",
      how_it_works: "So funktioniert's",
      manifesto: "Manifest",
      generation: "Generation 6AMICO",
      census: "Ort erfassen",
      request_access: "Zugang beantragen",
      map: "Dorfkarte",
      comuni: "Partnergemeinden",
      vetrina: "Lokale Vitrine",
      institutional: "Institutioneller Bereich",
      institutional_lead: "Für Gemeinden, Pro Loco und Partner",
      open: "Öffnen",
      close: "Schließen",
    },
    gdpr: { ...GDPR_IT, title: "Datenschutz", lead: "Granulare Einwilligung, Portabilität und Löschung (DSGVO).", download: "Meine Daten herunterladen (JSON)" },
  },
  es: {
    narration: {
      ...NARRATION_EN,
      listen: "Escuchar",
      pause: "Pausa",
      preparing: "Preparando la voz…",
      home_title: "Escuchar la home",
      home_hint: "Presentación en voz cálida — igual que los monumentos.",
      vocal_label: "Narración vocal",
    },
    nav: {
      home: "Inicio",
      villages: "Pueblos",
      qr: "Guías QR",
      piazze: "Plazas Sociales",
      merchants: "Vitrina",
      twinnings: "Hermanamientos",
      explore: "Explorar pueblos",
      events: "Eventos y Fiestas",
      network: "Red",
      world: "Mundo",
    },
    hero: {
      payoff: "ITALIAN TRAVEL EXPERIENCE",
      service_line: "MAPPING | HISTORY | HOSPITALITY | AIR | SEA | MOBILE",
      global_tagline: "YOUR DIGITAL GUIDE TO ITALY'S HIDDEN HERITAGE",
      kicker: "2013–2026 · TU CICERONE DIGITAL EN ITALIA",
      title: "Cada piedra tiene una historia. Descúbrela.",
      subtitle: "De los pueblos de Italia a las ciudades del mundo, la memoria de los lugares está a un clic.",
      sub: "De los pueblos de Italia a las ciudades del mundo, la memoria de los lugares está a un clic.",
      cta_primary: "Descubre los Pueblos",
      cta_explore: "Explorar",
      cta_secondary: "Escanear QR",
      qr_manual_toggle: "¿Tienes un código (p. ej. CAMPLI-001)? Introdúcelo aquí",
    },
    sections: {
      mission: "Nuestra Misión",
      mission_lead: "Hemos recuperado miles de textos e imágenes históricas de nuestros archivos.",
      villages: "Los Pueblos", villages_sub: "Cuatro puertas a la memoria de Abruzzo",
      qr: "Turismo Inteligente", qr_sub: "Escanea un QR en el territorio para desbloquear guías",
      piazze: "Plazas Sociales", piazze_sub: "Feed geolocalizado",
      merchants: "Economía Local", merchants_sub: "Una vitrina para comerciantes y productores",
      twinnings: "Hermanamiento Mundial", twinnings_sub: "Cada pueblo, una pequeña capital del mundo",
      chat: "Habla con 6amico", chat_sub: "La IA que conecta los pueblos, preserva la memoria y sugiere productos locales",
    },
    pages: {
      piazze: {
        kicker: "Red del Mérito · Italia",
        title: "Plaza Social",
        lead: "El feed compartido de todos los pueblos de la Red. Cada publicación es un recuerdo, una noticia o un aviso de los ciudadanos.",
        feed_aria: "Feed Plaza Social",
      },
      vetrina: {
        kicker: "MERCADO · TERRITORIO",
        search_placeholder: "Buscar un municipio… ej. Campli, Castiglione",
        featured: "Pueblos destacados",
        merchants_all: "Comerciantes de la red",
        merchants_for: "Vitrina · {name}",
        empty: "Sin resultados para este municipio. Prueba otro pueblo en la búsqueda.",
        filter_label: "Filtro",
        remove_filter: "Quitar",
      },
      eventi: {
        kicker: "CALENDARIO · TERRITORIO",
        title: "Eventos y Fiestas",
        lead: "Fiestas, ferias, encuentros y conciertos publicados por Pro Loco y municipios de la red.",
        suggest: "Sugerir un evento",
        loading: "Cargando...",
        empty_title: "Ningún evento próximo",
        empty_lead: "Las Pro Loco y los municipios publicarán pronto los próximos eventos.",
        cat_all: "Todos",
        cat_sagra: "Ferias",
        cat_festa: "Fiestas",
        cat_manifestazione: "Manifestaciones",
        cat_concerto: "Conciertos",
        cat_mostra: "Exposiciones",
        cat_religioso: "Religiosos",
        cat_sportivo: "Deportivos",
      },
    },
    ui: {
      read_more: "Leer más", discover: "Descubrir", visit: "Visitar", by: "a cargo de", year: "Año",
      code: "Código QR", scan_cta: "Introduce el código QR (ej. CAMPLI-001)", scan_button: "Abrir guía",
      contact: "Contactar", specialty: "Especialidad", featured: "Destacado", since: "Hermanado desde", country: "País",
      post: "Publicación", event: "Evento", news: "Noticia", new_post: "Comparte algo…", publish: "Publicar",
      your_name: "Tu nombre", choose_village: "Elegir pueblo", ask_placeholder: "Pregunta al Historiador…",
      send: "Enviar", open_chat: "Abrir chat", close: "Cerrar", view_all: "Ver todo",
      footer_tagline: "La Red Social del Territorio · 2013–2026", back_home: "Volver al inicio",
      landmarks: "Lugares icónicos", province: "Provincia", founded: "Fundado", population: "Habitantes",
      loading: "Cargando…", not_found: "Contenido no encontrado",
      frazioni: "Aldeas", monuments: "Monumentos", qr_download: "Descargar QR",
      qr_print: "Imprime el QR para instalarlo en el territorio",
      admin: "Admin", admin_login: "Iniciar sesión", password: "Contraseña", logout: "Salir",
      save: "Guardar", saving: "Guardando…", saved: "Guardado", ai_fill: "Generar con IA", generating: "Generando…",
      add_monument: "Añadir monumento", delete: "Eliminar", video: "Vídeo", paste_youtube: "Pega URL YouTube/Vimeo",
      gallery: "Galería", add_image_url: "URL imagen", add: "Añadir", edit: "Editar",
      no_frazioni: "Sin aldeas disponibles", explore_frazione: "Explorar",
            language: "Idioma",
      tabs: {
        storia: "Historia", storia_md: "Historia e información",
        frazioni: "Aldeas", frazioni_world: "Municipios",
        percorsi: "Rutas", percorsi_md: "Rutas y senderos",
        mangiare: "Comer", mangiare_md: "Dónde comer",
        dormire: "Dormir", dormire_md: "Dónde dormir",
        prodotti: "Productos", prodotti_md: "Productos locales", prodotti_world: "Especialidades", prodotti_world_md: "Productos y especialidades locales",
        luoghi: "Lugares", luoghi_md: "Lugares y cultura",
        proloco: "Pro Loco", proloco_md: "Pro Loco y eventos", proloco_world: "Eventos", proloco_world_md: "Eventos e instituciones",
        altro: "Más", movida: "Movida", movida_md: "Movida", sub: "Submarinismo", sub_md: "Submarinismo", barche: "Barcos",
        luoghi_cosa_vedere: "Qué ver", luoghi_chiese: "Iglesias", luoghi_musei: "Museos", luoghi_natura: "Naturaleza",
        culto: "Culto", culto_title: "Lugares de culto", monumenti: "Monumentos · Qué ver",
        chiama: "Llamar", sito: "Sitio", indicazioni: "Cómo llegar",
        link_istituzionale: "Enlace institucional", link_istituzionale_cultura: "Enlace institucional · cultura", video_istituzionale: "Vídeo · enlace institucional",
        utility: "Utilidades", sismica: "Sísmica", meteo: "Meteo",
        installa_app: "Instalar app", installa_title: "Instalar 6amico en iPhone",
        installa_intro: "Para tener 6amico a pantalla completa como una app real en tu iPhone:",
        installa_s1: "Toca el icono Compartir abajo en el centro de Safari (el cuadrado con la flecha hacia arriba).",
        installa_s2: "Desliza el menú y toca «Añadir a pantalla de inicio».",
        installa_s3: "Confirma con «Añadir» arriba a la derecha.",
        installa_done: "Entendido",
        installa_hint_desktop: "Para instalar la app, toca el icono de instalación o el «+» en la barra de direcciones del navegador.",
      },
    },
    footer: {
      blurb: "6amico valoriza los pueblos italianos: mapas, historias, eventos y productos típicos en una sola comunidad.",
      trust: "Privacidad · RGPD",
      nav_title: "Navegación",
      community_title: "Comunidad",
      discover_title: "Descubrir",
      welcome_tour: "Tour de bienvenida",
      how_it_works: "Cómo funciona",
      manifesto: "Manifiesto",
      generation: "Generación 6AMICO",
      census: "Registrar un lugar",
      request_access: "Solicitar acceso",
      map: "Mapa de pueblos",
      comuni: "Municipios amigos",
      vetrina: "Vitrina típica",
      institutional: "Área institucional",
      institutional_lead: "Para municipios, Pro Loco y socios",
      open: "Abrir",
      close: "Cerrar",
    },
    gdpr: { ...GDPR_IT, title: "Privacidad y datos", lead: "Consentimiento granular, portabilidad y supresión (RGPD).", download: "Descargar mis datos (JSON)" },
  },
};

/**
 * BLOCCO C — navbar/footer reali del sito attuale (NavbarTerritorialeTop +
 * FooterTerritorialeCompleto). Lingue con URL: it (default), en, fr, es.
 * TRADUZIONI AUTOMATICHE — marcate "da rivedere" da madrelingua prima
 * di considerarle definitive (revisione a cura di Paolo).
 */
const EXTRA_L10N = {
  it: {
    nav: {
      map3d: "Mappa 3D", login: "Accedi", login_or_register: "Accedi o registrati",
      more: "Altro", search_placeholder: "Cerca un borgo…", search_aria: "Cerca un borgo",
      tagline: "La rete dei borghi d'Italia · Dal 2013", ar_qr: "Visore AR & QR",
      logout: "Esci", passport: "Passaporto dei borghi",
    },
    hero: {
      cta_explore: "Esplora un borgo",
      cta_cartolina: "Ricevi la cartolina della domenica",
    },
    home_avi: {
      kicker: "Radici · Le tue origini",
      title: "Cerchi il paese dei tuoi avi?",
      lead: "Scrivi il nome del comune: ti portiamo alla sua scheda, con storia, foto, eventi e la sua piazza digitale.",
    },
    gsearch: {
      kicker: "Gemellaggi e città amiche",
      title: "Cerca un borgo o una città nel mondo",
      lead: "Scrivi il nome di un paese o di una città e scopri la sua scheda.",
      scope_aria: "Filtro ricerca (opzionale)", query_aria: "Nome borgo o città",
      scope_all: "Tutti",
      scope_it: "Italia · Borghi", scope_eu: "Europa · Città", scope_world: "Mondo · Città amiche",
      ph_all: "Es. Campli, Amalfi, Abidjan…",
      cta: "Cerca",
      err_none: "Nessun risultato. Prova con un altro nome o apri la mappa.",
      err_generic: "Ricerca non disponibile in questo momento. Riprova tra poco.",
      type_comune: "Borgo", type_frazione: "Frazione", type_city: "Città",
      suggested: "Borghi suggeriti:",
      link_mappa: "apri la mappa →",
      map_link: "Apri la mappa interattiva globale →",
    },
    cpass: {
      h1: "Racconta il tuo borgo. Noi lo mettiamo in evidenza.",
      lead: "Il Creator Pass 6amico è aperto a tutti i creator, anche con pochi follower: racconti borghi e città su TikTok, Instagram e YouTube.",
      benefits_h: "Cosa include il Pass",
      b_verified_t: "Spunta verificata e reel in evidenza",
      b_verified_x: "Il tuo profilo verificato e i tuoi reel migliori compaiono nella scheda del Comune.",
      b_musei_t: "Accesso a monumenti e musei — nei borghi aderenti",
      b_musei_x: "Entra senza biglietto nei luoghi del borgo che aderiscono al circuito 6amico.",
      b_food_t: "Degustazione di benvenuto — nei borghi aderenti",
      b_food_x: "Le botteghe del borgo ti accolgono con prodotti tipici da raccontare.",
      b_drone_t: "Mappa dei punti per riprese con drone",
      b_drone_x: "Le posizioni per le tue riprese, nel rispetto delle norme ENAC e delle regole di ogni comune.",
      steps_h: "Come funziona",
      step1_t: "Invii la candidatura",
      step1_x: "Compili il modulo qui sotto: bastano due minuti.",
      step2_t: "Ti rispondiamo via e-mail",
      step2_x: "Ti scriviamo all'indirizzo che hai indicato per confermare l'attivazione.",
      step3_t: "Racconti il borgo",
      step3_x: "Pubblichi i tuoi contenuti e noi mettiamo in evidenza il tuo lavoro nella scheda.",
      exchange: "In cambio ti chiediamo solo di citare 6amico e inserire il link della scheda del borgo nei tuoi contenuti.",
      trust_h: "Borghi aderenti e creator attivi",
      form_h: "Candidatura Creator Pass",
      form_sub: "Aperto a tutti i creator, anche con pochi follower.",
      f_platform: "Piattaforma principale",
      f_handle: "Nome utente (handle)",
      f_email: "E-mail",
      f_fascia: "Numero di follower",
      f_fascia_note: "Serve solo a conoscerti meglio: non è un requisito.",
      f_paese: "Paese (facoltativo)", f_paese_ph: "Es. Italia",
      f_borgo: "Borgo o città da raccontare", f_borgo_ph: "Es. Campli",
      f_note: "Note (facoltative)", f_note_ph: "Link al canale, tipo di contenuti, proposte…",
      f_privacy: "Ho letto l'informativa privacy", f_privacy_link: "Privacy",
      submit: "Invia candidatura",
      err_handle: "Inserisci il tuo nome utente.",
      err_email: "Inserisci un indirizzo e-mail valido.",
      err_borgo: "Indica il borgo che vuoi raccontare.",
      err_privacy: "Per inviare la candidatura devi accettare l'informativa privacy.",
      success_t: "Grazie! Candidatura inviata.",
      success_x: "Ti rispondiamo via e-mail entro 7 giorni.",
    },
    footx: {
      col_piazza: "Piazza & Storie", col_mappe: "Mappe & Sentieri",
      col_mondo: "Mondo & Alleanze", col_enti: "Enti & Assistenza",
      l_piazze: "Piazza Social", l_eventi: "Eventi & Sagre", l_manifesto: "Manifesto",
      l_regole: "Regole della Piazza", l_mappa3d: "Mappa 3D dei borghi",
      l_arqr: "Visore AR & QR", l_mappamondo: "Mappa del mondo", l_guideqr: "Guide QR",
      l_gemellaggi: "Gemellaggi", l_comuniamici: "Comuni amici", l_mondo: "Mondo",
      l_universita: "Università",
      l_origini: "Le tue origini", l_percomuni: "Per Comuni e Pro Loco", l_governance: "Come funziona 6amico",
      l_sindaci: "Area riservata ai Comuni", l_aderisci: "Adesione Enti & Università",
      l_accesso: "Richiedi un accesso", l_stampa: "Ufficio stampa",
      l_ambasciatore: "Scrivici",
      card_ar_t: "Visore AR & QR Code",
      card_ar_x: "Punta il telefono e apri i borghi in realtà aumentata.",
      card_gem_t: "Gemellaggi & Patti di Fratellanza",
      card_gem_x: "La rete internazionale dei borghi amici.",
      card_ist_t: "Istituzioni e territori",
      card_ist_x: "Il progetto è aperto alla collaborazione con comuni, Pro Loco, enti e università.",
      blurb: "Il social network dei borghi italiani: luoghi, storie, cammini e persone che li abitano.",
      payoff: "Il social network dei borghi italiani",
      cta_censisci: "+ Censisci un Luogo o un Sentiero",
      leg_chisiamo: "Chi siamo", leg_contatti: "Contatti", leg_privacy: "Privacy",
      leg_cookie: "Cookie", leg_termini: "Termini", leg_dati: "Dati Societari",
      nl_title: "La Cartolina della Domenica", nl_placeholder: "La tua email",
      nl_aria: "Iscriviti alla cartolina della domenica",
      nl_ok: "Iscrizione registrata — la prima cartolina arriva domenica mattina.",
      nl_offline: "Bozza salvata sul dispositivo: l'iscrizione arriverà presto.",
      nl_lang: "Lingua della cartolina",
      nl_consent_pre: "Ho letto la", nl_consent_link: "Privacy Policy",
      nl_consent_post: " e acconsento a ricevere la Cartolina della Domenica.",
      nl_tagline: "Ti scriviamo una volta a settimana",
      nl_pending: "Controlla la tua casella e clicca il link di conferma — la prima cartolina arriva domenica mattina.",
      nl_subscribe: "Iscriviti",
      nl_note_pre: "Ti scriviamo una volta a settimana. Puoi disiscriverti quando vuoi — vedi la",
      nl_note_link: "Privacy Policy",
      nl_note_post: ".",
      nl_error: "Iscrizione non riuscita — riprova tra poco.",
      nl_soon: "Le iscrizioni aprono a breve: stiamo attivando la conferma via e-mail. Torna a trovarci!",
      fb_follow: "Seguici su Facebook",
    },
    amb: {
      fab_hint: "La guida di 6amico — aiuto e contatti",
      cta: "Parla con l'Ambasciatore",
      welcome: "Benvenuto su 6amico. Sono la guida del territorio: posso aiutarti a scoprire borghi nascosti, sentieri, o metterti in contatto diretto con la nostra rete civica.",
      close: "Chiudi la chat",
    },
    share: {
      cta: "Condividi", copied: "Link copiato", copy_link: "Copia link", copy_prompt: "Copia questo link:",
      cancel: "Annulla", close: "Chiudi", open_options: "Apri opzioni di condivisione",
      piazza_post: "Pubblica sulla Piazza Social", piazza_label: "Piazza Social",
      piazza_of: "Piazza Social di {name}",
      piazza_lead: "Pubblicherai questo messaggio sul Wall pubblico di {name}. Tutti i cittadini del borgo lo vedranno.",
      published: "Pubblicato!",
      published_msg: "Il tuo messaggio è ora visibile sulla Piazza Social di {name} (+5 Punti Merito).",
      aura_needed: "⚡ Accendi la tua Aura per pubblicare sulla Piazza.",
      err_min_chars: "Scrivi almeno 5 caratteri prima di pubblicare.",
      err_publish: "Errore pubblicazione",
      publish: "Pubblica", publishing: "Pubblico…",
      default_title: "6amico — il social network dei borghi italiani",
      discover_on: "Scopri {name} su 6amico — il social network dei borghi italiani.",
      fb_preview: "anteprima",
    },
    home2: {
      hero_since: "Dal 2013 la tecnologia al servizio dei borghi d'Italia e delle città del mondo",
      bento_kicker: "Borghi faro", bento_title: "Sei territori da timbrare",
      bento_twin: "Gemellaggio con Castelli",
      bento_countdown: "VIII Centenario di San Francesco — mancano {days} giorni al 4 ottobre",
      bento_subasio: "Monte Subasio · tempo Tobler 3h 45m",
      bento_civ1: "Baluardo Borbonico · 600 m slm",
      bento_civ2: "La Ruetta, il vicolo più stretto",
      bento_civ3: "Filetto alla Borbonica",
      bento_castelli: "Maiolica, soffitto di San Donato, botteghe dei tornianti",
      bento_offida: "Merletto a tombolo, Rocca di Santa Maria, vini Pecorino DOCG",
      bento_montorio: "Fiume Vomano, Sentiero CAI 301, bocconotto caldo",
      bento_gps: "Sei a meno di 1.5 km? Sblocca il timbro araldico con GPS",
      vetrina_title: "Le città vetrina",
      vetrina_sub: "Schede territoriali complete, curate con comuni, Pro Loco e community.",
      vetrina_badge: "Vetrina",
      frazioni_title: "Le frazioni del territorio",
      frazioni_sub: "Borghi e frazioni con foto reali — Teramo pilota e Gran Sasso aquilano.",
      frazione_alt: "Frazione di {name}",
      event_kicker: "Evento in evidenza", event_cta_territorio: "Scopri il territorio",
      event_cta_sito: "Sito ufficiale", event_cta_all: "Tutti gli eventi",
      event_aria: "Evento in evidenza: {title}", events_tabs_aria: "Eventi in evidenza",
      event_tab_aria: "Evento in evidenza {i} di {n}",
      reels_kicker: "Reel verticali", reels_title: "I Borghi Raccontati dai Creator",
      reels_cta: "Sei un Creator? Carica il tuo Reel",
      reels_promo_t: "Pass Creator 6amico",
      reels_promo_x: "Per content creator con oltre 5k follower: accessi, degustazioni e badge verificato",
      reels_map3d: "Esplora Borgo sulla Mappa 3D", reels_close: "Chiudi player",
      reels_card_aria: "Reel di {handle} a {borgo}", reels_dialog_aria: "Player reel {handle}",
      portal_sr: "Percorsi principali", portal_cta: "Entra →",
      portal1_t: "ESPLORA LA MAPPA", portal1_d: "Borghi, itinerari e mappe interattive.",
      portal2_t: "RACCONTA IL TUO BORGO", portal2_d: "Aggiungi luoghi, foto e storie della tua comunità.",
      portal3_t: "LA PIAZZA DIGITALE", portal3_d: "Unisciti alla community che valorizza i borghi.",
      progetto_title: "Il progetto 6amico",
      progetto_sub: "Storia, strumenti e community — per valorizzare i borghi insieme.",
      progetto_open: "Apri",
      progetto1_t: "Le nostre radici", progetto1_d: "Come nasce 6amico, dal 2013 a oggi",
      progetto2_t: "Progetti del territorio", progetto2_d: "Iniziative e percorsi certificati",
      progetto3_t: "Come funziona", progetto3_d: "Guide, memoria e community in breve",
      progetto4_t: "Il manifesto", progetto4_d: "Unisciti alla community dei borghi",
      tech_h1: "Tecnologia al servizio del territorio",
      tech_lead: "6amico collega borghi, memoria e community: scopri, racconta e vivi il territorio in pochi click.",
      tech1_label: "Guide intelligenti", tech1_t: "Scopri i luoghi con lo smartphone",
      tech1_b: "Inquadra un QR sul monumento e apri subito storia, foto e punti vicini.",
      tech1_s1t: "Scheda in un tap", tech1_s1d: "Guida curata del luogo, senza app da scaricare.",
      tech1_s2t: "Mappa del territorio", tech1_s2d: "Borghi, sentieri e attrazioni collegati sulla stessa mappa.",
      tech2_label: "Turismo e territorio", tech2_t: "Borghi collegati in Italia e in Europa",
      tech2_b: "Gemellaggi e percorsi condivisi per far conoscere i comuni italiani a chi viaggia.",
      tech2_s1t: "Gemellaggi vivi", tech2_s1d: "Storie e luoghi in comune tra città amiche.",
      tech2_s2t: "Promozione locale", tech2_s2d: "Eventi e prodotti tipici visibili a turisti e residenti.",
      tech3_label: "La piazza digitale", tech3_t: "La community del borgo",
      tech3_b: "Foto, eventi e racconti pubblicati da cittadini, Pro Loco e comuni.",
      tech3_link: "Vai alla Piazza Social",
      tech4_label: "Memoria viva", tech4_t: "Storia e AI al tuo servizio",
      tech4_b: "Archivi storici e suggerimenti intelligenti per valorizzare ogni borgo.",
      tech4_link: "Come funziona",
      cic_loading: "Cicerone digitale · rilevamento territorio…",
      cic_welcome: "Benvenuto a {name}!",
      cic_welcome_sub: "Apri la scheda per mappe, storie e cosa vedere oggi.",
      cic_open: "Apri scheda",
      cic_reco_title: "Borghi consigliati",
      cic_reco_sub_a: "Altri borghi del network e mete raccomandate, incluso il gemellaggio culturale Abruzzo–Umbria tra Castelli e Assisi.",
      cic_reco_sub_b: "Mete raccomandate e borghi del network — il Presepe di Castelli collega l'Abruzzo al Sacro Convento di Assisi.",
      cic_week: "Borgo della settimana",
      cic_explore: "Esplora",
      cic_view_network: "Tutto il Network",
      cic_view_prefix: "Vista:",
      cic_geo_off: "filtro prossimità locale disattivato.",
      banner_lead: "Scopri mappe, storie e punti di interesse vivi in rete.",
      mon_kicker: "Monumento narrante · Cicerone",
      mon_near: "A circa {m} m da te",
      mon_narration: "Sei vicino a {name}. Ascolta la narrazione del Cicerone digitale.",
      mon_open: "Apri scheda completa",
      scroll_prev: "Borghi precedenti", scroll_next: "Borghi successivi",
      scroll_aria: "Scorri borghi",
    },
    ponte: {
      kicker: "6amico Globale · Ponte Virtuale Interregionale e Internazionale",
      title: "Dai Borghi d'Italia alle Sfide Mondiali",
      sub: "Superare i vecchi gemellaggi di carta con connessioni civiche live.",
      planisfero: "Il planisfero dei corridoi & gemellaggi",
      img_alt: "Il borgo 6amico al centro dei ponti virtuali: rotte verso l'hub Abidjan (porto), Napoli (grandi eventi), Assisi (rete di pace UNESCO) e il corridoio della diaspora italiana New York–Buenos Aires",
      abidjan_tag: "Corridoio Attivo",
      abidjan_desc: "Corridoio civico e logistico attivo Mosciano ↔ Abidjan — il ponte 6amico già tracciato con l'Africa occidentale.",
      napoli_tag: "Grandi Eventi",
      napoli_desc: "Grandi eventi e flussi velici mondiali — il fronte mare come porta d'ingresso ai borghi.",
      assisi_tag: "Rete di Pace",
      assisi_desc: "Ottocentenario Francescano 2026 e Rete di Pace — il nodo spirituale della rete UNESCO.",
      diaspora_tag: "Turismo Radici",
      diaspora_desc: "Gli italiani all'estero e il turismo delle radici — da New York a Buenos Aires, ritorno ai borghi.",
      cta_ponte: "Vedi Ponte", cta_rete: "Vedi Rete",
      dipl_kicker: "Diplomazia territoriale",
      dipl_title: "Sei un'Ambasciata, un Consolato o un Comune con gemellaggi attivi?",
      dipl_cta: "Proponi un Nuovo Ponte Virtuale con 6amico",
      dipl_note: "mondo@6amico.com · canale relazioni internazionali",
    },
    scopri: {
      active: "Scheda attiva", pending: "In attesa di Custode",
      title: "Scopri anche", official: "Rete ufficiale · turismo",
      servizi: "Servizi pratici · accessi e trasporti", section_aria: "Collegamenti territorio",
    },
  },
  en: {
    nav: {
      map3d: "3D Map", login: "Sign in", login_or_register: "Sign in or register",
      more: "More", search_placeholder: "Search a village…", search_aria: "Search a village",
      tagline: "The network of Italy's villages · Since 2013", ar_qr: "AR & QR viewer",
      logout: "Log out", passport: "Village passport",
    },
    hero: {
      cta_explore: "Explore a village",
      cta_cartolina: "Get the Sunday postcard",
    },
    home_avi: {
      kicker: "Roots · Your origins",
      title: "Looking for your ancestors' village?",
      lead: "Type the name of the municipality: we'll take you to its page, with history, photos, events and its digital square.",
    },
    gsearch: {
      kicker: "Twinnings and sister cities",
      title: "Search a village or a city worldwide",
      lead: "Type the name of a village or a city and discover its page.",
      scope_aria: "Search filter (optional)", query_aria: "Village or city name",
      scope_all: "All",
      scope_it: "Italy · Villages", scope_eu: "Europe · Cities", scope_world: "World · Sister cities",
      ph_all: "E.g. Campli, Amalfi, Abidjan…",
      cta: "Search",
      err_none: "No results. Try another name or open the map.",
      err_generic: "Search is not available right now. Please try again shortly.",
      type_comune: "Village", type_frazione: "Hamlet", type_city: "City",
      suggested: "Suggested villages:",
      link_mappa: "open the map →",
      map_link: "Open the interactive world map →",
    },
    cpass: {
      h1: "Tell the story of your village. We'll spotlight it.",
      lead: "The 6amico Creator Pass is open to all creators, even with a small following: you tell villages and cities on TikTok, Instagram and YouTube.",
      benefits_h: "What the Pass includes",
      b_verified_t: "Verified badge and featured reels",
      b_verified_x: "Your verified profile and best reels appear on the municipality page.",
      b_musei_t: "Access to monuments and museums — in participating villages",
      b_musei_x: "Free entry to the places in villages that joined the 6amico network.",
      b_food_t: "Welcome tasting — in participating villages",
      b_food_x: "The village's shops welcome you with local products to tell about.",
      b_drone_t: "Map of drone filming spots",
      b_drone_x: "Authorised filming spots, in compliance with ENAC rules and each municipality's regulations.",
      steps_h: "How it works",
      step1_t: "Send your application",
      step1_x: "Fill in the form below — it takes two minutes.",
      step2_t: "We reply by e-mail",
      step2_x: "We write to the address you provided to confirm your activation.",
      step3_t: "You tell the village",
      step3_x: "You publish your content and we feature your work on the page.",
      exchange: "In return we only ask you to mention 6amico and link the village page in your content.",
      trust_h: "Participating villages and active creators",
      form_h: "Creator Pass application",
      form_sub: "Open to all creators, even with a small following.",
      f_platform: "Main platform",
      f_handle: "Username (handle)",
      f_email: "E-mail",
      f_fascia: "Number of followers",
      f_fascia_note: "It only helps us know you better — not a requirement.",
      f_paese: "Country (optional)", f_paese_ph: "E.g. Italy",
      f_borgo: "Village or city to cover", f_borgo_ph: "E.g. Campli",
      f_note: "Notes (optional)", f_note_ph: "Channel link, content type, ideas…",
      f_privacy: "I have read the privacy policy", f_privacy_link: "Privacy",
      submit: "Send application",
      err_handle: "Enter your username.",
      err_email: "Enter a valid e-mail address.",
      err_borgo: "Tell us the village you want to cover.",
      err_privacy: "To send your application you must accept the privacy policy.",
      success_t: "Thank you! Application sent.",
      success_x: "We will reply by e-mail within 7 days.",
    },
    footx: {
      col_piazza: "Square & Stories", col_mappe: "Maps & Trails",
      col_mondo: "World & Alliances", col_enti: "Institutions & Help",
      l_piazze: "Social Square", l_eventi: "Events & Festivals", l_manifesto: "Manifesto",
      l_regole: "Square rules", l_mappa3d: "3D village map",
      l_arqr: "AR & QR viewer", l_mappamondo: "World map", l_guideqr: "QR Guides",
      l_gemellaggi: "Twinnings", l_comuniamici: "Partner towns", l_mondo: "World",
      l_universita: "Universities",
      l_origini: "Your roots", l_percomuni: "For Municipalities & Pro Loco", l_governance: "How 6amico works",
      l_sindaci: "Municipalities area", l_aderisci: "Institutions & Universities membership",
      l_accesso: "Request access", l_stampa: "Press office",
      l_ambasciatore: "Write to us",
      card_ar_t: "AR & QR Code viewer",
      card_ar_x: "Point your phone and open the villages in augmented reality.",
      card_gem_t: "Twinnings & Brotherhood Pacts",
      card_gem_x: "The international network of friendly villages.",
      card_ist_t: "Institutions and territories",
      card_ist_x: "The project is open to collaboration with municipalities, Pro Loco, bodies and universities.",
      blurb: "The social network of Italian villages: places, stories, trails and the people who live there.",
      payoff: "The social network of Italy's villages",
      cta_censisci: "+ Map a Place or a Trail",
      leg_chisiamo: "About us", leg_contatti: "Contacts", leg_privacy: "Privacy",
      leg_cookie: "Cookies", leg_termini: "Terms", leg_dati: "Company details",
      nl_title: "The Sunday Postcard", nl_placeholder: "Your email",
      nl_aria: "Subscribe to the Sunday Postcard",
      nl_ok: "Subscription registered — the first postcard arrives Sunday morning.",
      nl_offline: "Draft saved on your device: the subscription will arrive soon.",
      nl_lang: "Postcard language",
      nl_consent_pre: "I have read the", nl_consent_link: "Privacy Policy",
      nl_consent_post: " and I consent to receiving the Sunday Postcard.",
      nl_tagline: "We write to you once a week",
      nl_pending: "Check your inbox and click the confirmation link — your first postcard arrives Sunday morning.",
      nl_subscribe: "Subscribe",
      nl_note_pre: "We write once a week. Unsubscribe anytime — see the",
      nl_note_link: "Privacy Policy",
      nl_note_post: ".",
      nl_error: "Subscription failed — please try again shortly.",
      nl_soon: "Subscriptions open soon: we are enabling e-mail confirmation. Check back!",
      fb_follow: "Follow us on Facebook",
    },
    amb: {
      fab_hint: "6amico's guide — help and contacts",
      cta: "Talk to the Ambassador",
      welcome: "Welcome to 6amico. I'm the territory guide: I can help you discover hidden villages, trails, or put you in direct contact with our civic network.",
      close: "Close the chat",
    },
    share: {
      cta: "Share", copied: "Link copied", copy_link: "Copy link", copy_prompt: "Copy this link:",
      cancel: "Cancel", close: "Close", open_options: "Open share options",
      piazza_post: "Post to the Social Square", piazza_label: "Social Square",
      piazza_of: "{name} Social Square",
      piazza_lead: "You'll post this message on {name}'s public Wall. All the village's citizens will see it.",
      published: "Published!",
      published_msg: "Your message is now visible on {name}'s Social Square (+5 Merit Points).",
      aura_needed: "⚡ Turn on your Aura to post on the Square.",
      err_min_chars: "Write at least 5 characters before publishing.",
      err_publish: "Publishing failed",
      publish: "Publish", publishing: "Publishing…",
      default_title: "6amico — the social network of Italian villages",
      discover_on: "Discover {name} on 6amico — the social network of Italian villages.",
      fb_preview: "preview",
    },
    home2: {
      hero_since: "Since 2013, technology at the service of Italy's villages and the world's cities",
      bento_kicker: "Beacon villages", bento_title: "Six territories to stamp",
      bento_twin: "Twinning with Castelli",
      bento_countdown: "8th Centenary of Saint Francis — {days} days to October 4th",
      bento_subasio: "Monte Subasio · Tobler time 3h 45m",
      bento_civ1: "Bourbon Bastion · 600 m a.s.l.",
      bento_civ2: "La Ruetta, the narrowest alley",
      bento_civ3: "Filetto alla Borbonica",
      bento_castelli: "Majolica, San Donato ceiling, potters' workshops",
      bento_offida: "Bobbin lace, Rocca di Santa Maria, Pecorino DOCG wines",
      bento_montorio: "Vomano river, CAI trail 301, warm bocconotto",
      bento_gps: "Within 1.5 km? Unlock the heraldic stamp with GPS",
      vetrina_title: "Showcase cities",
      vetrina_sub: "Complete territorial pages, curated with municipalities, Pro Loco and community.",
      vetrina_badge: "Showcase",
      frazioni_title: "Villages of the territory",
      frazioni_sub: "Villages and hamlets with real photos — Teramo pilot and the L'Aquila Gran Sasso.",
      frazione_alt: "Hamlet of {name}",
      event_kicker: "Featured event", event_cta_territorio: "Discover the territory",
      event_cta_sito: "Official website", event_cta_all: "All events",
      event_aria: "Featured event: {title}", events_tabs_aria: "Featured events",
      event_tab_aria: "Featured event {i} of {n}",
      reels_kicker: "Vertical reels", reels_title: "Villages told by Creators",
      reels_cta: "Are you a Creator? Upload your Reel",
      reels_promo_t: "6amico Creator Pass",
      reels_promo_x: "For content creators with 5k+ followers: access, tastings and a verified badge",
      reels_map3d: "Explore the village on the 3D Map", reels_close: "Close player",
      reels_card_aria: "Reel by {handle} in {borgo}", reels_dialog_aria: "Reel player {handle}",
      portal_sr: "Main paths", portal_cta: "Enter →",
      portal1_t: "EXPLORE THE MAP", portal1_d: "Villages, itineraries and interactive maps.",
      portal2_t: "TELL YOUR VILLAGE", portal2_d: "Add places, photos and stories of your community.",
      portal3_t: "THE DIGITAL SQUARE", portal3_d: "Join the community that celebrates villages.",
      progetto_title: "The 6amico project",
      progetto_sub: "History, tools and community — to celebrate villages together.",
      progetto_open: "Open",
      progetto1_t: "Our roots", progetto1_d: "How 6amico was born, from 2013 to today",
      progetto2_t: "Territory projects", progetto2_d: "Certified initiatives and routes",
      progetto3_t: "How it works", progetto3_d: "Guides, memory and community in brief",
      progetto4_t: "The manifesto", progetto4_d: "Join the villages community",
      tech_h1: "Technology at the service of the territory",
      tech_lead: "6amico connects villages, memory and community: discover, tell and experience the territory in a few clicks.",
      tech1_label: "Smart guides", tech1_t: "Discover places with your smartphone",
      tech1_b: "Scan a QR code on the monument and instantly open history, photos and nearby points.",
      tech1_s1t: "Page in one tap", tech1_s1d: "Curated guide to the place, no app to download.",
      tech1_s2t: "Territory map", tech1_s2d: "Villages, trails and attractions connected on the same map.",
      tech2_label: "Tourism and territory", tech2_t: "Villages connected in Italy and Europe",
      tech2_b: "Twinnings and shared routes to show Italian municipalities to travellers.",
      tech2_s1t: "Living twinnings", tech2_s1d: "Stories and places shared between sister cities.",
      tech2_s2t: "Local promotion", tech2_s2d: "Events and local products visible to tourists and residents.",
      tech3_label: "The digital square", tech3_t: "The village community",
      tech3_b: "Photos, events and stories published by citizens, Pro Loco and municipalities.",
      tech3_link: "Go to the Social Square",
      tech4_label: "Living memory", tech4_t: "History and AI at your service",
      tech4_b: "Historical archives and smart suggestions to celebrate every village.",
      tech4_link: "How it works",
      cic_loading: "Digital Cicerone · detecting territory…",
      cic_welcome: "Welcome to {name}!",
      cic_welcome_sub: "Open the page for maps, stories and what to see today.",
      cic_open: "Open page",
      cic_reco_title: "Recommended villages",
      cic_reco_sub_a: "More villages in the network and recommended destinations, including the Abruzzo–Umbria cultural twinning between Castelli and Assisi.",
      cic_reco_sub_b: "Recommended destinations and network villages — the Castelli Nativity connects Abruzzo to the Sacro Convento of Assisi.",
      cic_week: "Village of the week",
      cic_explore: "Explore",
      cic_view_network: "The whole Network",
      cic_view_prefix: "View:",
      cic_geo_off: "local proximity filter off.",
      banner_lead: "Discover maps, stories and live points of interest in the network.",
      mon_kicker: "Narrating monument · Cicerone",
      mon_near: "About {m} m from you",
      mon_narration: "You are near {name}. Listen to the digital Cicerone narration.",
      mon_open: "Open full page",
      scroll_prev: "Previous villages", scroll_next: "Next villages",
      scroll_aria: "Scroll villages",
    },
    ponte: {
      kicker: "6amico Global · Interregional and International Virtual Bridge",
      title: "From Italy's Villages to World Challenges",
      sub: "Going beyond old paper twinnings with live civic connections.",
      planisfero: "The world map of corridors & twinnings",
      img_alt: "The 6amico village at the centre of virtual bridges: routes to the Abidjan hub (port), Naples (major events), Assisi (UNESCO peace network) and the Italian diaspora corridor New York–Buenos Aires",
      abidjan_tag: "Active Corridor",
      abidjan_desc: "Active civic & logistics corridor Mosciano ↔ Abidjan — the 6amico bridge already live to West Africa.",
      napoli_tag: "Major Events",
      napoli_desc: "Major events and world sailing flows — the waterfront as gateway to the villages.",
      assisi_tag: "Peace Network",
      assisi_desc: "Franciscan 800th Anniversary 2026 and Peace Network — the spiritual node of the UNESCO network.",
      diaspora_tag: "Roots Tourism",
      diaspora_desc: "Italians abroad and roots tourism — from New York to Buenos Aires, back to the villages.",
      cta_ponte: "See Bridge", cta_rete: "See Network",
      dipl_kicker: "Territorial diplomacy",
      dipl_title: "Are you an Embassy, a Consulate or a Municipality with active twinnings?",
      dipl_cta: "Propose a New Virtual Bridge with 6amico",
      dipl_note: "mondo@6amico.com · international relations channel",
    },
    scopri: {
      active: "Live page", pending: "Awaiting a Custode",
      title: "Discover more", official: "Official network · tourism",
      servizi: "Practical services · access and transport", section_aria: "Territory links",
    },
  },
  fr: {
    nav: {
      map3d: "Carte 3D", login: "Se connecter", login_or_register: "Se connecter ou s'inscrire",
      more: "Plus", search_placeholder: "Chercher un village…", search_aria: "Chercher un village",
      tagline: "Le réseau des villages d'Italie · Depuis 2013", ar_qr: "Visionneuse AR & QR",
      logout: "Déconnexion", passport: "Passeport des villages",
    },
    hero: {
      cta_explore: "Explorer un village",
      cta_cartolina: "Recevoir la carte postale du dimanche",
    },
    home_avi: {
      kicker: "Racines · Vos origines",
      title: "Vous cherchez le village de vos ancêtres ?",
      lead: "Tapez le nom de la commune : nous vous emmenons sur sa page, avec histoire, photos, événements et sa place numérique.",
    },
    gsearch: {
      kicker: "Jumelages et villes amies",
      title: "Recherchez un village ou une ville dans le monde",
      lead: "Écrivez le nom d'un village ou d'une ville et découvrez sa page.",
      scope_aria: "Filtre de recherche (optionnel)", query_aria: "Nom du village ou de la ville",
      scope_all: "Tous",
      scope_it: "Italie · Villages", scope_eu: "Europe · Villes", scope_world: "Monde · Villes amies",
      ph_all: "Ex. Campli, Amalfi, Abidjan…",
      cta: "Rechercher",
      err_none: "Aucun résultat. Essayez un autre nom ou ouvrez la carte.",
      err_generic: "La recherche n'est pas disponible pour le moment. Réessayez dans un instant.",
      type_comune: "Village", type_frazione: "Hameau", type_city: "Ville",
      suggested: "Villages suggérés :",
      link_mappa: "ouvrir la carte →",
      map_link: "Ouvrir la carte interactive du monde →",
    },
    cpass: {
      h1: "Racontez votre village. Nous le mettons en avant.",
      lead: "Le Creator Pass 6amico est ouvert à tous les créateurs, même avec peu d'abonnés : vous racontez villages et villes sur TikTok, Instagram et YouTube.",
      benefits_h: "Ce que le Pass comprend",
      b_verified_t: "Badge vérifié et reels en avant",
      b_verified_x: "Votre profil vérifié et vos meilleurs reels apparaissent sur la page de la commune.",
      b_musei_t: "Accès aux monuments et musées — dans les villages participants",
      b_musei_x: "Entrée gratuite dans les lieux des villages ayant rejoint le réseau 6amico.",
      b_food_t: "Dégustation de bienvenue — dans les villages participants",
      b_food_x: "Les boutiques du village vous accueillent avec des produits locaux à raconter.",
      b_drone_t: "Carte des spots de tournage drone",
      b_drone_x: "Les positions autorisées pour vos prises de vue, dans le respect des normes ENAC et des règles de chaque commune.",
      steps_h: "Comment ça marche",
      step1_t: "Vous envoyez votre candidature",
      step1_x: "Remplissez le formulaire ci-dessous : deux minutes suffisent.",
      step2_t: "Nous répondons par e-mail",
      step2_x: "Nous écrivons à l'adresse indiquée pour confirmer l'activation.",
      step3_t: "Vous racontez le village",
      step3_x: "Vous publiez vos contenus et nous mettons votre travail en avant sur la page.",
      exchange: "En échange, nous vous demandons seulement de mentionner 6amico et d'ajouter le lien de la page du village dans vos contenus.",
      trust_h: "Villages participants et créateurs actifs",
      form_h: "Candidature Creator Pass",
      form_sub: "Ouvert à tous les créateurs, même avec peu d'abonnés.",
      f_platform: "Plateforme principale",
      f_handle: "Nom d'utilisateur (handle)",
      f_email: "E-mail",
      f_fascia: "Nombre d'abonnés",
      f_fascia_note: "Cela sert juste à mieux vous connaître — ce n'est pas un critère.",
      f_paese: "Pays (facultatif)", f_paese_ph: "Ex. Italie",
      f_borgo: "Village ou ville à raconter", f_borgo_ph: "Ex. Campli",
      f_note: "Notes (facultatives)", f_note_ph: "Lien de la chaîne, type de contenus, idées…",
      f_privacy: "J'ai lu la politique de confidentialité", f_privacy_link: "Confidentialité",
      submit: "Envoyer la candidature",
      err_handle: "Saisissez votre nom d'utilisateur.",
      err_email: "Saisissez une adresse e-mail valide.",
      err_borgo: "Indiquez le village que vous voulez raconter.",
      err_privacy: "Pour envoyer votre candidature, vous devez accepter la politique de confidentialité.",
      success_t: "Merci ! Candidature envoyée.",
      success_x: "Nous répondons par e-mail sous 7 jours.",
    },
    footx: {
      col_piazza: "Place & Histoires", col_mappe: "Cartes & Sentiers",
      col_mondo: "Monde & Alliances", col_enti: "Institutions & Aide",
      l_piazze: "Place Sociale", l_eventi: "Événements & Fêtes", l_manifesto: "Manifeste",
      l_regole: "Règles de la Place", l_mappa3d: "Carte 3D des villages",
      l_arqr: "Visionneuse AR & QR", l_mappamondo: "Carte du monde", l_guideqr: "Guides QR",
      l_gemellaggi: "Jumelages", l_comuniamici: "Communes amies", l_mondo: "Monde",
      l_universita: "Universités",
      l_origini: "Vos origines", l_percomuni: "Pour communes et Pro Loco", l_governance: "Comment fonctionne 6amico",
      l_sindaci: "Espace réservé aux communes", l_aderisci: "Adhésion Institutions & Universités",
      l_accesso: "Demander un accès", l_stampa: "Bureau de presse",
      l_ambasciatore: "Écrivez-nous",
      card_ar_t: "Visionneuse AR & QR Code",
      card_ar_x: "Pointez votre téléphone et ouvrez les villages en réalité augmentée.",
      card_gem_t: "Jumelages & Pactes de Fraternité",
      card_gem_x: "Le réseau international des villages amis.",
      card_ist_t: "Institutions et territoires",
      card_ist_x: "Le projet est ouvert à la collaboration avec les communes, les Pro Loco, les organismes et les universités.",
      blurb: "Le réseau social des villages italiens : lieux, histoires, sentiers et les gens qui les habitent.",
      payoff: "Le réseau social des villages d'Italie",
      cta_censisci: "+ Recenser un Lieu ou un Sentier",
      leg_chisiamo: "Qui sommes-nous", leg_contatti: "Contacts", leg_privacy: "Confidentialité",
      leg_cookie: "Cookies", leg_termini: "Conditions", leg_dati: "Mentions légales",
      nl_title: "La Carte Postale du Dimanche", nl_placeholder: "Votre email",
      nl_aria: "S'abonner à la carte postale du dimanche",
      nl_ok: "Inscription enregistrée — la première carte arrive dimanche matin.",
      nl_offline: "Brouillon enregistré sur l'appareil : l'inscription arrivera bientôt.",
      nl_lang: "Langue de la carte postale",
      nl_consent_pre: "J'ai lu la", nl_consent_link: "Politique de confidentialité",
      nl_consent_post: " et j'accepte de recevoir la Carte postale du dimanche.",
      nl_tagline: "Nous vous écrivons une fois par semaine",
      nl_pending: "Vérifiez votre boîte mail et cliquez sur le lien de confirmation — votre première carte arrive dimanche matin.",
      nl_subscribe: "S'inscrire",
      nl_note_pre: "Nous écrivons une fois par semaine. Désinscription à tout moment — voir la",
      nl_note_link: "Politique de confidentialité",
      nl_note_post: ".",
      nl_error: "Inscription impossible — réessayez dans un instant.",
      nl_soon: "Les inscriptions ouvrent bientôt : nous activons la confirmation par e-mail. Revenez vite !",
      fb_follow: "Suivez-nous sur Facebook",
    },
    amb: {
      fab_hint: "Le guide de 6amico — aide et contacts",
      cta: "Parler à l'Ambassadeur",
      welcome: "Bienvenue sur 6amico. Je suis le guide du territoire : je peux vous aider à découvrir des villages cachés, des sentiers, ou vous mettre en contact direct avec notre réseau civique.",
      close: "Fermer le chat",
    },
    share: {
      cta: "Partager", copied: "Lien copié", copy_link: "Copier le lien", copy_prompt: "Copiez ce lien :",
      cancel: "Annuler", close: "Fermer", open_options: "Ouvrir les options de partage",
      piazza_post: "Publier sur la Place Sociale", piazza_label: "Place Sociale",
      piazza_of: "Place Sociale de {name}",
      piazza_lead: "Vous publierez ce message sur le mur public de {name}. Tous les citoyens du village le verront.",
      published: "Publié !",
      published_msg: "Votre message est maintenant visible sur la Place Sociale de {name} (+5 Points de Mérite).",
      aura_needed: "⚡ Activez votre Aura pour publier sur la Place.",
      err_min_chars: "Écrivez au moins 5 caractères avant de publier.",
      err_publish: "Échec de la publication",
      publish: "Publier", publishing: "Publication…",
      default_title: "6amico — le réseau social des villages italiens",
      discover_on: "Découvrez {name} sur 6amico — le réseau social des villages italiens.",
      fb_preview: "aperçu",
    },
    home2: {
      hero_since: "Depuis 2013, la technologie au service des villages d'Italie et des villes du monde",
      bento_kicker: "Villages phares", bento_title: "Six territoires à tamponner",
      bento_twin: "Jumelage avec Castelli",
      bento_countdown: "VIIIe Centenaire de saint François — encore {days} jours avant le 4 octobre",
      bento_subasio: "Monte Subasio · temps Tobler 3h 45m",
      bento_civ1: "Bastion Bourbon · 600 m d'altitude",
      bento_civ2: "La Ruetta, la ruelle la plus étroite",
      bento_civ3: "Filetto alla Borbonica",
      bento_castelli: "Majolique, plafond de San Donato, ateliers de tourneurs",
      bento_offida: "Dentelle au fuseau, Rocca di Santa Maria, vins Pecorino DOCG",
      bento_montorio: "Rivière Vomano, sentier CAI 301, bocconotto chaud",
      bento_gps: "À moins de 1,5 km ? Débloquez le tampon héraldique avec le GPS",
      vetrina_title: "Les villes vitrines",
      vetrina_sub: "Fiches territoriales complètes, élaborées avec les communes, les Pro Loco et la communauté.",
      vetrina_badge: "Vitrine",
      frazioni_title: "Les hameaux du territoire",
      frazioni_sub: "Villages et hameaux avec de vraies photos — Teramo pilote et le Gran Sasso de L'Aquila.",
      frazione_alt: "Hameau de {name}",
      event_kicker: "Événement à la une", event_cta_territorio: "Découvrir le territoire",
      event_cta_sito: "Site officiel", event_cta_all: "Tous les événements",
      event_aria: "Événement à la une : {title}", events_tabs_aria: "Événements à la une",
      event_tab_aria: "Événement à la une {i} sur {n}",
      reels_kicker: "Reels verticaux", reels_title: "Les Villages racontés par les Créateurs",
      reels_cta: "Vous êtes Créateur ? Publiez votre Reel",
      reels_promo_t: "Pass Créateur 6amico",
      reels_promo_x: "Pour les créateurs de contenu avec plus de 5k abonnés : accès, dégustations et badge vérifié",
      reels_map3d: "Explorer le village sur la Carte 3D", reels_close: "Fermer le lecteur",
      reels_card_aria: "Reel de {handle} à {borgo}", reels_dialog_aria: "Lecteur reel {handle}",
      portal_sr: "Parcours principaux", portal_cta: "Entrer →",
      portal1_t: "EXPLORER LA CARTE", portal1_d: "Villages, itinéraires et cartes interactives.",
      portal2_t: "RACONTEZ VOTRE VILLAGE", portal2_d: "Ajoutez lieux, photos et histoires de votre communauté.",
      portal3_t: "LA PLACE NUMÉRIQUE", portal3_d: "Rejoignez la communauté qui valorise les villages.",
      progetto_title: "Le projet 6amico",
      progetto_sub: "Histoire, outils et communauté — pour valoriser les villages ensemble.",
      progetto_open: "Ouvrir",
      progetto1_t: "Nos racines", progetto1_d: "Comment 6amico est né, de 2013 à aujourd'hui",
      progetto2_t: "Projets du territoire", progetto2_d: "Initiatives et parcours certifiés",
      progetto3_t: "Comment ça marche", progetto3_d: "Guides, mémoire et communauté en bref",
      progetto4_t: "Le manifeste", progetto4_d: "Rejoignez la communauté des villages",
      tech_h1: "La technologie au service du territoire",
      tech_lead: "6amico relie villages, mémoire et communauté : découvrez, racontez et vivez le territoire en quelques clics.",
      tech1_label: "Guides intelligents", tech1_t: "Découvrez les lieux avec votre smartphone",
      tech1_b: "Scannez un QR code sur le monument et ouvrez aussitôt histoire, photos et points voisins.",
      tech1_s1t: "Fiche en un clic", tech1_s1d: "Guide soigné du lieu, sans application à télécharger.",
      tech1_s2t: "Carte du territoire", tech1_s2d: "Villages, sentiers et attractions reliés sur la même carte.",
      tech2_label: "Tourisme et territoire", tech2_t: "Villages reliés en Italie et en Europe",
      tech2_b: "Jumelages et parcours partagés pour faire connaître les communes italiennes aux voyageurs.",
      tech2_s1t: "Jumelages vivants", tech2_s1d: "Histoires et lieux partagés entre villes amies.",
      tech2_s2t: "Promotion locale", tech2_s2d: "Événements et produits typiques visibles aux touristes et résidents.",
      tech3_label: "La place numérique", tech3_t: "La communauté du village",
      tech3_b: "Photos, événements et récits publiés par citoyens, Pro Loco et communes.",
      tech3_link: "Aller à la Place Sociale",
      tech4_label: "Mémoire vivante", tech4_t: "Histoire et IA à votre service",
      tech4_b: "Archives historiques et suggestions intelligentes pour valoriser chaque village.",
      tech4_link: "Comment ça marche",
      cic_loading: "Cicerone numérique · détection du territoire…",
      cic_welcome: "Bienvenue à {name} !",
      cic_welcome_sub: "Ouvrez la fiche pour les cartes, les histoires et quoi voir aujourd'hui.",
      cic_open: "Ouvrir la fiche",
      cic_reco_title: "Villages recommandés",
      cic_reco_sub_a: "D'autres villages du réseau et des destinations recommandées, dont le jumelage culturel Abruzzes–Ombrie entre Castelli et Assise.",
      cic_reco_sub_b: "Destinations recommandées et villages du réseau — la Crèche de Castelli relie les Abruzzes au Sacro Convento d'Assise.",
      cic_week: "Village de la semaine",
      cic_explore: "Explorer",
      cic_view_network: "Tout le Réseau",
      cic_view_prefix: "Vue :",
      cic_geo_off: "filtre de proximité locale désactivé.",
      banner_lead: "Découvrez cartes, histoires et points d'intérêt vivants du réseau.",
      mon_kicker: "Monument narrateur · Cicerone",
      mon_near: "À environ {m} m de vous",
      mon_narration: "Vous êtes près de {name}. Écoutez la narration du Cicerone numérique.",
      mon_open: "Ouvrir la fiche complète",
      scroll_prev: "Villages précédents", scroll_next: "Villages suivants",
      scroll_aria: "Faire défiler les villages",
    },
    ponte: {
      kicker: "6amico Global · Pont Virtuel Interrégional et International",
      title: "Des Villages d'Italie aux Défis Mondiaux",
      sub: "Dépasser les vieux jumelages sur papier avec des connexions civiques en direct.",
      planisfero: "Le planisphère des corridors & jumelages",
      img_alt: "Le village 6amico au centre des ponts virtuels : routes vers le hub Abidjan (port), Naples (grands événements), Assise (réseau de paix UNESCO) et le corridor de la diaspora italienne New York–Buenos Aires",
      abidjan_tag: "Corridor Actif",
      abidjan_desc: "Corridor civique et logistique actif Mosciano ↔ Abidjan — le pont 6amico déjà tracé vers l'Afrique occidentale.",
      napoli_tag: "Grands Événements",
      napoli_desc: "Grands événements et flux de voile mondiaux — le front de mer comme porte d'entrée des villages.",
      assisi_tag: "Réseau de Paix",
      assisi_desc: "800e anniversaire franciscain 2026 et Réseau de Paix — le nœud spirituel du réseau UNESCO.",
      diaspora_tag: "Tourisme des Racines",
      diaspora_desc: "Les Italiens à l'étranger et le tourisme des racines — de New York à Buenos Aires, retour aux villages.",
      cta_ponte: "Voir le Pont", cta_rete: "Voir le Réseau",
      dipl_kicker: "Diplomatie territoriale",
      dipl_title: "Vous êtes une Ambassade, un Consulat ou une Commune avec des jumelages actifs ?",
      dipl_cta: "Proposez un Nouveau Pont Virtuel avec 6amico",
      dipl_note: "mondo@6amico.com · canal relations internationales",
    },
    scopri: {
      active: "Fiche active", pending: "En attente d'un Custode",
      title: "À découvrir aussi", official: "Réseau officiel · tourisme",
      servizi: "Services pratiques · accès et transports", section_aria: "Liens du territoire",
    },
  },
  es: {
    nav: {
      map3d: "Mapa 3D", login: "Entrar", login_or_register: "Entrar o registrarse",
      more: "Más", search_placeholder: "Buscar un pueblo…", search_aria: "Buscar un pueblo",
      tagline: "La red de los pueblos de Italia · Desde 2013", ar_qr: "Visor AR y QR",
      logout: "Salir", passport: "Pasaporte de los pueblos",
    },
    hero: {
      cta_explore: "Explora un pueblo",
      cta_cartolina: "Recibir la postal del domingo",
    },
    home_avi: {
      kicker: "Raíces · Tus orígenes",
      title: "¿Buscas el pueblo de tus antepasados?",
      lead: "Escribe el nombre del municipio: te llevamos a su página, con historia, fotos, eventos y su plaza digital.",
    },
    gsearch: {
      kicker: "Hermanamientos y ciudades amigas",
      title: "Busca un pueblo o una ciudad en el mundo",
      lead: "Escribe el nombre de un pueblo o de una ciudad y descubre su ficha.",
      scope_aria: "Filtro de búsqueda (opcional)", query_aria: "Nombre del pueblo o de la ciudad",
      scope_all: "Todos",
      scope_it: "Italia · Pueblos", scope_eu: "Europa · Ciudades", scope_world: "Mundo · Ciudades amigas",
      ph_all: "Ej. Campli, Amalfi, Abiyán…",
      cta: "Buscar",
      err_none: "Sin resultados. Prueba con otro nombre o abre el mapa.",
      err_generic: "La búsqueda no está disponible en este momento. Inténtalo de nuevo en breve.",
      type_comune: "Pueblo", type_frazione: "Aldea", type_city: "Ciudad",
      suggested: "Pueblos sugeridos:",
      link_mappa: "abrir el mapa →",
      map_link: "Abrir el mapa interactivo del mundo →",
    },
    cpass: {
      h1: "Cuenta tu pueblo. Nosotros lo destacamos.",
      lead: "El Creator Pass 6amico está abierto a todos los creadores, incluso con pocos seguidores: cuentas pueblos y ciudades en TikTok, Instagram y YouTube.",
      benefits_h: "Qué incluye el Pass",
      b_verified_t: "Insignia verificada y reels destacados",
      b_verified_x: "Tu perfil verificado y tus mejores reels aparecen en la ficha del municipio.",
      b_musei_t: "Acceso a monumentos y museos — en los pueblos participantes",
      b_musei_x: "Entrada gratuita a los lugares de los pueblos adheridos a la red 6amico.",
      b_food_t: "Degustación de bienvenida — en los pueblos participantes",
      b_food_x: "Las tiendas del pueblo te reciben con productos locales que contar.",
      b_drone_t: "Mapa de puntos para grabar con dron",
      b_drone_x: "Las posiciones autorizadas para tus grabaciones, respetando las normas ENAC y las reglas de cada municipio.",
      steps_h: "Cómo funciona",
      step1_t: "Envías tu candidatura",
      step1_x: "Rellenas el formulario de abajo: solo dos minutos.",
      step2_t: "Te respondemos por e-mail",
      step2_x: "Te escribimos a la dirección indicada para confirmar la activación.",
      step3_t: "Cuentas el pueblo",
      step3_x: "Publicas tu contenido y nosotros destacamos tu trabajo en la ficha.",
      exchange: "A cambio solo te pedimos mencionar a 6amico e incluir el enlace de la ficha del pueblo en tus contenidos.",
      trust_h: "Pueblos participantes y creadores activos",
      form_h: "Candidatura Creator Pass",
      form_sub: "Abierto a todos los creadores, incluso con pocos seguidores.",
      f_platform: "Plataforma principal",
      f_handle: "Nombre de usuario (handle)",
      f_email: "E-mail",
      f_fascia: "Número de seguidores",
      f_fascia_note: "Solo sirve para conocerte mejor: no es un requisito.",
      f_paese: "País (opcional)", f_paese_ph: "Ej. Italia",
      f_borgo: "Pueblo o ciudad a contar", f_borgo_ph: "Ej. Campli",
      f_note: "Notas (opcionales)", f_note_ph: "Enlace al canal, tipo de contenidos, propuestas…",
      f_privacy: "He leído la política de privacidad", f_privacy_link: "Privacidad",
      submit: "Enviar candidatura",
      err_handle: "Introduce tu nombre de usuario.",
      err_email: "Introduce una dirección de e-mail válida.",
      err_borgo: "Indica el pueblo que quieres contar.",
      err_privacy: "Para enviar la candidatura debes aceptar la política de privacidad.",
      success_t: "¡Gracias! Candidatura enviada.",
      success_x: "Te respondemos por e-mail en 7 días.",
    },
    footx: {
      col_piazza: "Plaza e Historias", col_mappe: "Mapas y Senderos",
      col_mondo: "Mundo y Alianzas", col_enti: "Instituciones y Ayuda",
      l_piazze: "Plaza Social", l_eventi: "Eventos y Fiestas", l_manifesto: "Manifiesto",
      l_regole: "Reglas de la Plaza", l_mappa3d: "Mapa 3D de los pueblos",
      l_arqr: "Visor AR y QR", l_mappamondo: "Mapa del mundo", l_guideqr: "Guías QR",
      l_gemellaggi: "Hermanamientos", l_comuniamici: "Municipios amigos", l_mondo: "Mundo",
      l_universita: "Universidades",
      l_origini: "Tus orígenes", l_percomuni: "Para municipios y Pro Loco", l_governance: "Cómo funciona 6amico",
      l_sindaci: "Área reservada a los municipios", l_aderisci: "Adhesión Instituciones y Universidades",
      l_accesso: "Solicitar acceso", l_stampa: "Oficina de prensa",
      l_ambasciatore: "Escríbenos",
      card_ar_t: "Visor AR y Código QR",
      card_ar_x: "Apunta el teléfono y abre los pueblos en realidad aumentada.",
      card_gem_t: "Hermanamientos y Pactos de Fraternidad",
      card_gem_x: "La red internacional de pueblos amigos.",
      card_ist_t: "Instituciones y territorios",
      card_ist_x: "El proyecto está abierto a la colaboración con municipios, Pro Loco, organismos y universidades.",
      blurb: "La red social de los pueblos italianos: lugares, historias, senderos y las personas que los habitan.",
      payoff: "La red social de los pueblos de Italia",
      cta_censisci: "+ Registrar un Lugar o un Sendero",
      leg_chisiamo: "Quiénes somos", leg_contatti: "Contacto", leg_privacy: "Privacidad",
      leg_cookie: "Cookies", leg_termini: "Términos", leg_dati: "Datos de la empresa",
      nl_title: "La Postal del Domingo", nl_placeholder: "Tu email",
      nl_aria: "Suscribirse a la postal del domingo",
      nl_ok: "Suscripción registrada — la primera postal llega el domingo por la mañana.",
      nl_offline: "Borrador guardado en el dispositivo: la suscripción llegará pronto.",
      nl_lang: "Idioma de la postal",
      nl_consent_pre: "He leído la", nl_consent_link: "Política de privacidad",
      nl_consent_post: " y acepto recibir la Postal del Domingo.",
      nl_tagline: "Te escribimos una vez a la semana",
      nl_pending: "Revisa tu bandeja y haz clic en el enlace de confirmación — tu primera postal llega el domingo por la mañana.",
      nl_subscribe: "Suscribirme",
      nl_note_pre: "Escribimos una vez a la semana. Date de baja cuando quieras — consulta la",
      nl_note_link: "Política de privacidad",
      nl_note_post: ".",
      nl_error: "Suscripción fallida — inténtalo de nuevo en breve.",
      nl_soon: "Las suscripciones abren pronto: estamos activando la confirmación por e-mail. ¡Vuelve a visitarnos!",
      fb_follow: "Síguenos en Facebook",
    },
    amb: {
      fab_hint: "La guía de 6amico — ayuda y contactos",
      cta: "Habla con el Embajador",
      welcome: "Bienvenido a 6amico. Soy la guía del territorio: puedo ayudarte a descubrir pueblos escondidos, senderos, o ponerte en contacto directo con nuestra red cívica.",
      close: "Cerrar el chat",
    },
    share: {
      cta: "Compartir", copied: "Enlace copiado", copy_link: "Copiar enlace", copy_prompt: "Copia este enlace:",
      cancel: "Cancelar", close: "Cerrar", open_options: "Abrir opciones para compartir",
      piazza_post: "Publicar en la Plaza Social", piazza_label: "Plaza Social",
      piazza_of: "Plaza Social de {name}",
      piazza_lead: "Publicarás este mensaje en el muro público de {name}. Todos los ciudadanos del pueblo lo verán.",
      published: "¡Publicado!",
      published_msg: "Tu mensaje ya es visible en la Plaza Social de {name} (+5 Puntos de Mérito).",
      aura_needed: "⚡ Enciende tu Aura para publicar en la Plaza.",
      err_min_chars: "Escribe al menos 5 caracteres antes de publicar.",
      err_publish: "Error al publicar",
      publish: "Publicar", publishing: "Publicando…",
      default_title: "6amico — la red social de los pueblos italianos",
      discover_on: "Descubre {name} en 6amico — la red social de los pueblos italianos.",
      fb_preview: "vista previa",
    },
    home2: {
      hero_since: "Desde 2013, tecnología al servicio de los pueblos de Italia y las ciudades del mundo",
      bento_kicker: "Pueblos faro", bento_title: "Seis territorios para sellar",
      bento_twin: "Hermanamiento con Castelli",
      bento_countdown: "VIII Centenario de San Francisco — faltan {days} días para el 4 de octubre",
      bento_subasio: "Monte Subasio · tiempo Tobler 3h 45m",
      bento_civ1: "Baluarte Borbónico · 600 m s. n. m.",
      bento_civ2: "La Ruetta, el callejón más estrecho",
      bento_civ3: "Filetto alla Borbonica",
      bento_castelli: "Mayólica, techo de San Donato, talleres de torneros",
      bento_offida: "Encaje de bolillos, Rocca di Santa Maria, vinos Pecorino DOCG",
      bento_montorio: "Río Vomano, sendero CAI 301, bocconotto caliente",
      bento_gps: "¿Estás a menos de 1,5 km? Desbloquea el sello heráldico con GPS",
      vetrina_title: "Las ciudades escaparate",
      vetrina_sub: "Fichas territoriales completas, elaboradas con municipios, Pro Loco y comunidad.",
      vetrina_badge: "Escaparate",
      frazioni_title: "Las aldeas del territorio",
      frazioni_sub: "Pueblos y aldeas con fotos reales — Teramo piloto y el Gran Sasso de L'Aquila.",
      frazione_alt: "Aldea de {name}",
      event_kicker: "Evento destacado", event_cta_territorio: "Descubre el territorio",
      event_cta_sito: "Sitio oficial", event_cta_all: "Todos los eventos",
      event_aria: "Evento destacado: {title}", events_tabs_aria: "Eventos destacados",
      event_tab_aria: "Evento destacado {i} de {n}",
      reels_kicker: "Reels verticales", reels_title: "Los Pueblos contados por los Creators",
      reels_cta: "¿Eres Creator? Sube tu Reel",
      reels_promo_t: "Pase Creator 6amico",
      reels_promo_x: "Para creadores de contenido con más de 5k seguidores: accesos, degustaciones y badge verificado",
      reels_map3d: "Explora el pueblo en el Mapa 3D", reels_close: "Cerrar reproductor",
      reels_card_aria: "Reel de {handle} en {borgo}", reels_dialog_aria: "Reproductor de reel {handle}",
      portal_sr: "Rutas principales", portal_cta: "Entrar →",
      portal1_t: "EXPLORA EL MAPA", portal1_d: "Pueblos, itinerarios y mapas interactivos.",
      portal2_t: "CUENTA TU PUEBLO", portal2_d: "Añade lugares, fotos e historias de tu comunidad.",
      portal3_t: "LA PLAZA DIGITAL", portal3_d: "Únete a la comunidad que valora los pueblos.",
      progetto_title: "El proyecto 6amico",
      progetto_sub: "Historia, herramientas y comunidad — para valorar los pueblos juntos.",
      progetto_open: "Abrir",
      progetto1_t: "Nuestras raíces", progetto1_d: "Cómo nace 6amico, de 2013 a hoy",
      progetto2_t: "Proyectos del territorio", progetto2_d: "Iniciativas y recorridos certificados",
      progetto3_t: "Cómo funciona", progetto3_d: "Guías, memoria y comunidad en breve",
      progetto4_t: "El manifiesto", progetto4_d: "Únete a la comunidad de los pueblos",
      tech_h1: "Tecnología al servicio del territorio",
      tech_lead: "6amico conecta pueblos, memoria y comunidad: descubre, cuenta y vive el territorio en pocos clics.",
      tech1_label: "Guías inteligentes", tech1_t: "Descubre los lugares con el smartphone",
      tech1_b: "Escanea un QR en el monumento y abre al instante historia, fotos y puntos cercanos.",
      tech1_s1t: "Ficha en un toque", tech1_s1d: "Guía curada del lugar, sin app que descargar.",
      tech1_s2t: "Mapa del territorio", tech1_s2d: "Pueblos, senderos y atracciones conectados en el mismo mapa.",
      tech2_label: "Turismo y territorio", tech2_t: "Pueblos conectados en Italia y Europa",
      tech2_b: "Hermanamientos y recorridos compartidos para dar a conocer los municipios italianos a quien viaja.",
      tech2_s1t: "Hermanamientos vivos", tech2_s1d: "Historias y lugares en común entre ciudades amigas.",
      tech2_s2t: "Promoción local", tech2_s2d: "Eventos y productos típicos visibles para turistas y residentes.",
      tech3_label: "La plaza digital", tech3_t: "La comunidad del pueblo",
      tech3_b: "Fotos, eventos y relatos publicados por ciudadanos, Pro Loco y municipios.",
      tech3_link: "Ir a la Plaza Social",
      tech4_label: "Memoria viva", tech4_t: "Historia e IA a tu servicio",
      tech4_b: "Archivos históricos y sugerencias inteligentes para valorar cada pueblo.",
      tech4_link: "Cómo funciona",
      cic_loading: "Cicerone digital · detectando territorio…",
      cic_welcome: "¡Bienvenido a {name}!",
      cic_welcome_sub: "Abre la ficha para mapas, historias y qué ver hoy.",
      cic_open: "Abrir ficha",
      cic_reco_title: "Pueblos recomendados",
      cic_reco_sub_a: "Otros pueblos de la red y destinos recomendados, incluido el hermanamiento cultural Abruzzo–Umbría entre Castelli y Assisi.",
      cic_reco_sub_b: "Destinos recomendados y pueblos de la red — el Belén de Castelli conecta Abruzzo con el Sacro Convento de Assisi.",
      cic_week: "Pueblo de la semana",
      cic_explore: "Explorar",
      cic_view_network: "Toda la Red",
      cic_view_prefix: "Vista:",
      cic_geo_off: "filtro de proximidad local desactivado.",
      banner_lead: "Descubre mapas, historias y puntos de interés vivos en la red.",
      mon_kicker: "Monumento narrador · Cicerone",
      mon_near: "A unos {m} m de ti",
      mon_narration: "Estás cerca de {name}. Escucha la narración del Cicerone digital.",
      mon_open: "Abrir ficha completa",
      scroll_prev: "Pueblos anteriores", scroll_next: "Pueblos siguientes",
      scroll_aria: "Desplazar pueblos",
    },
    ponte: {
      kicker: "6amico Global · Puente Virtual Interregional e Internacional",
      title: "De los Pueblos de Italia a los Desafíos Mundiales",
      sub: "Superar los viejos hermanamientos de papel con conexiones cívicas en vivo.",
      planisfero: "El planisferio de los corredores & hermanamientos",
      img_alt: "El pueblo 6amico en el centro de los puentes virtuales: rutas hacia el hub Abiyán (puerto), Nápoles (grandes eventos), Asís (red de paz UNESCO) y el corredor de la diáspora italiana Nueva York–Buenos Aires",
      abidjan_tag: "Corredor Activo",
      abidjan_desc: "Corredor cívico y logístico activo Mosciano ↔ Abidjan — el puente 6amico ya trazado hacia África occidental.",
      napoli_tag: "Grandes Eventos",
      napoli_desc: "Grandes eventos y flujos de vela mundiales — el frente marítimo como puerta de entrada a los pueblos.",
      assisi_tag: "Red de Paz",
      assisi_desc: "Octocentenario Franciscano 2026 y Red de Paz — el nodo espiritual de la red UNESCO.",
      diaspora_tag: "Turismo de Raíces",
      diaspora_desc: "Los italianos en el extranjero y el turismo de raíces — de Nueva York a Buenos Aires, regreso a los pueblos.",
      cta_ponte: "Ver Puente", cta_rete: "Ver Red",
      dipl_kicker: "Diplomacia territorial",
      dipl_title: "¿Eres una Embajada, un Consulado o un Municipio con hermanamientos activos?",
      dipl_cta: "Propón un Nuevo Puente Virtual con 6amico",
      dipl_note: "mondo@6amico.com · canal de relaciones internacionales",
    },
    scopri: {
      active: "Ficha activa", pending: "En espera de Custode",
      title: "Descubre también", official: "Red oficial · turismo",
      servizi: "Servicios prácticos · accesos y transportes", section_aria: "Enlaces del territorio",
    },
  },
  // Tedesco: solo lato client (Google Translate aid) — /de/ non indicizzato. Da rivedere.
  de: {
    hero: {
      cta_explore: "Ein Dorf entdecken",
      cta_cartolina: "Die Sonntagspostkarte erhalten",
    },
    home_avi: {
      kicker: "Wurzeln · Ihre Herkunft",
      title: "Suchen Sie das Dorf Ihrer Vorfahren?",
      lead: "Geben Sie den Namen der Gemeinde ein: Wir bringen Sie zu ihrer Seite mit Geschichte, Fotos, Veranstaltungen und ihrem digitalen Platz.",
    },
    footx: {
      nl_subscribe: "Anmelden",
      payoff: "Das soziale Netzwerk der italienischen Dörfer",
      nl_title: "Die Sonntagspostkarte",
      nl_tagline: "Wir schreiben Ihnen einmal pro Woche",
      nl_soon: "Die Anmeldungen starten bald: Wir aktivieren gerade die Bestätigung per E-Mail. Schauen Sie bald wieder vorbei!",
      fb_follow: "Folgen Sie uns auf Facebook",
    },
    amb: {
      fab_hint: "Der 6amico-Guide — Hilfe und Kontakte",
      cta: "Mit dem Botschafter sprechen",
      welcome: "Willkommen bei 6amico. Ich bin der Gebietsführer: Ich helfe Ihnen, versteckte Dörfer und Wanderwege zu entdecken oder Sie mit unserem Bürgernetzwerk zu verbinden.",
      close: "Chat schließen",
    },
    cpass: {
      h1: "Erzählen Sie Ihr Dorf. Wir stellen es in den Vordergrund.",
      lead: "Der 6amico Creator Pass steht allen Creators offen, auch mit wenigen Followern: Sie erzählen Dörfer und Städte auf TikTok, Instagram und YouTube.",
      benefits_h: "Was der Pass enthält",
      b_verified_t: "Verifiziertes Abzeichen und hervorgehobene Reels",
      b_verified_x: "Ihr verifiziertes Profil und Ihre besten Reels erscheinen auf der Gemeindeseite.",
      steps_h: "So funktioniert es",
      step1_t: "Sie senden Ihre Bewerbung",
      step1_x: "Füllen Sie das Formular unten aus — zwei Minuten genügen.",
      step2_t: "Wir antworten per E-Mail",
      step2_x: "Wir schreiben an die angegebene Adresse, um die Aktivierung zu bestätigen.",
      step3_t: "Sie erzählen das Dorf",
      step3_x: "Sie veröffentlichen Ihre Inhalte und wir stellen Ihre Arbeit auf der Seite heraus.",
      exchange: "Im Gegenzug bitten wir Sie nur, 6amico zu erwähnen und den Link zur Dorfseite in Ihren Inhalten zu teilen.",
      trust_h: "Teilnehmende Dörfer und aktive Creators",
      form_h: "Creator-Pass-Bewerbung",
      form_sub: "Offen für alle Creators, auch mit wenigen Followern.",
      f_platform: "Hauptplattform",
      f_handle: "Benutzername (Handle)",
      f_email: "E-Mail",
      f_fascia: "Follower-Zahl",
      f_fascia_note: "Dient nur dazu, Sie besser kennenzulernen — keine Voraussetzung.",
      f_paese: "Land (optional)", f_paese_ph: "Z. B. Italien",
      f_borgo: "Dorf oder Stadt, über die Sie berichten", f_borgo_ph: "Z. B. Campli",
      f_note: "Anmerkungen (optional)", f_note_ph: "Kanal-Link, Inhaltstyp, Ideen…",
      f_privacy: "Ich habe die Datenschutzerklärung gelesen", f_privacy_link: "Datenschutz",
      submit: "Bewerbung senden",
      err_handle: "Geben Sie Ihren Benutzernamen ein.",
      err_email: "Geben Sie eine gültige E-Mail-Adresse ein.",
      err_borgo: "Geben Sie das Dorf an, über das Sie berichten möchten.",
      err_privacy: "Um die Bewerbung zu senden, müssen Sie die Datenschutzerklärung akzeptieren.",
      success_t: "Danke! Bewerbung gesendet.",
      success_x: "Wir antworten per E-Mail innerhalb von 7 Tagen.",
    },
    share: {
      cta: "Teilen", copied: "Link kopiert", copy_link: "Link kopieren", copy_prompt: "Diesen Link kopieren:",
      cancel: "Abbrechen", close: "Schließen", open_options: "Teilen-Optionen öffnen",
      piazza_post: "Auf dem Sozialen Platz veröffentlichen", piazza_label: "Sozialer Platz",
      piazza_of: "Sozialer Platz von {name}",
      piazza_lead: "Sie veröffentlichen diese Nachricht auf der öffentlichen Pinnwand von {name}. Alle Bürger des Dorfes sehen sie.",
      published: "Veröffentlicht!",
      published_msg: "Ihre Nachricht ist jetzt auf dem Sozialen Platz von {name} sichtbar (+5 Verdienstpunkte).",
      aura_needed: "⚡ Aktivieren Sie Ihre Aura, um auf dem Platz zu veröffentlichen.",
      err_min_chars: "Schreiben Sie mindestens 5 Zeichen vor dem Veröffentlichen.",
      err_publish: "Veröffentlichung fehlgeschlagen",
      publish: "Veröffentlichen", publishing: "Veröffentliche…",
      default_title: "6amico — das soziale Netzwerk der italienischen Dörfer",
      discover_on: "Entdecken Sie {name} auf 6amico — das soziale Netzwerk der italienischen Dörfer.",
      fb_preview: "Vorschau",
    },
    home2: {
      hero_since: "Seit 2013 Technologie im Dienst der Dörfer Italiens und der Städte der Welt",
      bento_kicker: "Leuchtturm-Dörfer", bento_title: "Sechs Gebiete zum Abstempeln",
      bento_twin: "Partnerschaft mit Castelli",
      bento_countdown: "800. Franziskus-Jubiläum — noch {days} Tage bis zum 4. Oktober",
      bento_subasio: "Monte Subasio · Tobler-Zeit 3h 45m",
      bento_civ1: "Bourbonen-Bollwerk · 600 m ü. M.",
      bento_civ2: "La Ruetta, die engste Gasse",
      bento_civ3: "Filetto alla Borbonica",
      bento_castelli: "Majolika, San-Donato-Decke, Drechslerwerkstätten",
      bento_offida: "Klöppelspitze, Rocca di Santa Maria, Pecorino-DOCG-Weine",
      bento_montorio: "Vomano-Fluss, CAI-Weg 301, warmer Bocconotto",
      bento_gps: "Weniger als 1,5 km entfernt? Schalten Sie den heraldischen Stempel per GPS frei",
      vetrina_title: "Die Schaufenster-Städte",
      vetrina_sub: "Vollständige Gebietsseiten, erstellt mit Gemeinden, Pro Loco und Community.",
      vetrina_badge: "Schaufenster",
      frazioni_title: "Die Ortsteile des Gebiets",
      frazioni_sub: "Dörfer und Ortsteile mit echten Fotos — Pilot Teramo und der L'Aquilaer Gran Sasso.",
      frazione_alt: "Ortsteil von {name}",
      event_kicker: "Empfohlenes Event", event_cta_territorio: "Das Gebiet entdecken",
      event_cta_sito: "Offizielle Website", event_cta_all: "Alle Events",
      event_aria: "Empfohlenes Event: {title}", events_tabs_aria: "Empfohlene Events",
      event_tab_aria: "Empfohlenes Event {i} von {n}",
      reels_kicker: "Vertikale Reels", reels_title: "Die Dörfer erzählt von Creators",
      reels_cta: "Sind Sie Creator? Laden Sie Ihr Reel hoch",
      reels_promo_t: "6amico Creator-Pass",
      reels_promo_x: "Für Content-Creator mit über 5k Followern: Zugänge, Verkostungen und verifiziertes Abzeichen",
      reels_map3d: "Dorf auf der 3D-Karte erkunden", reels_close: "Player schließen",
      reels_card_aria: "Reel von {handle} in {borgo}", reels_dialog_aria: "Reel-Player {handle}",
      portal_sr: "Hauptwege", portal_cta: "Eintreten →",
      portal1_t: "KARTE ERKUNDEN", portal1_d: "Dörfer, Routen und interaktive Karten.",
      portal2_t: "ERZÄHLEN SIE IHR DORF", portal2_d: "Fügen Sie Orte, Fotos und Geschichten Ihrer Gemeinde hinzu.",
      portal3_t: "DER DIGITALE PLATZ", portal3_d: "Treten Sie der Community bei, die die Dörfer würdigt.",
      progetto_title: "Das 6amico-Projekt",
      progetto_sub: "Geschichte, Werkzeuge und Community — um die Dörfer gemeinsam zu würdigen.",
      progetto_open: "Öffnen",
      progetto1_t: "Unsere Wurzeln", progetto1_d: "Wie 6amico entstand, von 2013 bis heute",
      progetto2_t: "Gebietsprojekte", progetto2_d: "Zertifizierte Initiativen und Wege",
      progetto3_t: "So funktioniert es", progetto3_d: "Guides, Erinnerung und Community in Kürze",
      progetto4_t: "Das Manifest", progetto4_d: "Treten Sie der Dorf-Community bei",
      tech_h1: "Technologie im Dienst des Gebiets",
      tech_lead: "6amico verbindet Dörfer, Erinnerung und Community: entdecken, erzählen und erleben Sie das Gebiet in wenigen Klicks.",
      tech1_label: "Intelligente Guides", tech1_t: "Orte mit dem Smartphone entdecken",
      tech1_b: "Scannen Sie einen QR-Code am Denkmal und öffnen Sie sofort Geschichte, Fotos und nahe Punkte.",
      tech1_s1t: "Seite mit einem Tap", tech1_s1d: "Kuratierte Anleitung des Ortes, ohne App-Download.",
      tech1_s2t: "Karte des Gebiets", tech1_s2d: "Dörfer, Wege und Attraktionen auf derselben Karte verbunden.",
      tech2_label: "Tourismus und Gebiet", tech2_t: "Dörfer verbunden in Italien und Europa",
      tech2_b: "Partnerschaften und gemeinsame Routen, um italienische Gemeinden Reisenden bekannt zu machen.",
      tech2_s1t: "Lebendige Partnerschaften", tech2_s1d: "Geschichten und Orte, die Partnerstädte teilen.",
      tech2_s2t: "Lokale Förderung", tech2_s2d: "Events und typische Produkte sichtbar für Touristen und Einwohner.",
      tech3_label: "Der digitale Platz", tech3_t: "Die Dorf-Community",
      tech3_b: "Fotos, Events und Erzählungen von Bürgern, Pro Loco und Gemeinden.",
      tech3_link: "Zum Sozialen Platz",
      tech4_label: "Lebendige Erinnerung", tech4_t: "Geschichte und KI zu Ihren Diensten",
      tech4_b: "Historische Archive und intelligente Vorschläge, um jedes Dorf zu würdigen.",
      tech4_link: "So funktioniert es",
      cic_loading: "Digitaler Cicerone · Gebiet wird erkannt…",
      cic_welcome: "Willkommen in {name}!",
      cic_welcome_sub: "Öffnen Sie die Seite für Karten, Geschichten und was es heute zu sehen gibt.",
      cic_open: "Seite öffnen",
      cic_reco_title: "Empfohlene Dörfer",
      cic_reco_sub_a: "Weitere Dörfer des Netzwerks und empfohlene Ziele, darunter die kulturelle Partnerschaft Abruzzen–Umbrien zwischen Castelli und Assisi.",
      cic_reco_sub_b: "Empfohlene Ziele und Netzwerk-Dörfer — die Krippe von Castelli verbindet die Abruzzen mit dem Sacro Convento von Assisi.",
      cic_week: "Dorf der Woche",
      cic_explore: "Erkunden",
      cic_view_network: "Das ganze Netzwerk",
      cic_view_prefix: "Ansicht:",
      cic_geo_off: "lokaler Näherungsfilter deaktiviert.",
      banner_lead: "Entdecken Sie Karten, Geschichten und lebendige Sehenswürdigkeiten im Netz.",
      mon_kicker: "Erzählendes Denkmal · Cicerone",
      mon_near: "Etwa {m} m von Ihnen entfernt",
      mon_narration: "Sie sind in der Nähe von {name}. Hören Sie die Erzählung des digitalen Cicerone.",
      mon_open: "Vollständige Seite öffnen",
      scroll_prev: "Vorherige Dörfer", scroll_next: "Nächste Dörfer",
      scroll_aria: "Dörfer scrollen",
    },
    ponte: {
      kicker: "6amico Global · Virtuelle Interregionale und Internationale Brücke",
      title: "Von den Dörfern Italiens zu den Welt-Herausforderungen",
      sub: "Über alte Papier-Partnerschaften hinaus — mit lebendigen bürgerlichen Verbindungen.",
      planisfero: "Der Planisphäre der Korridore & Partnerschaften",
      img_alt: "Das 6amico-Dorf im Zentrum der virtuellen Brücken: Routen zum Hub Abidjan (Hafen), Neapel (große Events), Assisi (UNESCO-Friedensnetz) und dem Korridor der italienischen Diaspora New York–Buenos Aires",
      abidjan_tag: "Aktiver Korridor",
      abidjan_desc: "Aktiver bürgerschaftlicher und logistischer Korridor Mosciano ↔ Abidjan — die bereits verfolgte 6amico-Brücke nach Westafrika.",
      napoli_tag: "Große Events",
      napoli_desc: "Große Events und weltweite Segelflüsse — die Strandfront als Eingangstor zu den Dörfern.",
      assisi_tag: "Friedensnetz",
      assisi_desc: "800. Franziskus-Jubiläum 2026 und Friedensnetz — der spirituelle Knoten des UNESCO-Netzes.",
      diaspora_tag: "Wurzel-Tourismus",
      diaspora_desc: "Italiener im Ausland und Wurzeltourismus — von New York bis Buenos Aires, Rückkehr zu den Dörfern.",
      cta_ponte: "Brücke ansehen", cta_rete: "Netz ansehen",
      dipl_kicker: "Territoriale Diplomatie",
      dipl_title: "Sind Sie eine Botschaft, ein Konsulat oder eine Gemeinde mit aktiven Partnerschaften?",
      dipl_cta: "Schlagen Sie eine Neue Virtuelle Brücke mit 6amico vor",
      dipl_note: "mondo@6amico.com · Kanal für internationale Beziehungen",
    },
    scopri: {
      active: "Aktive Seite", pending: "Wartet auf einen Custode",
      title: "Entdecke auch", official: "Offizielles Netz · Tourismus",
      servizi: "Praktische Dienste · Zugang und Verkehr", section_aria: "Gebietslinks",
    },
  },
};
for (const _lc of Object.keys(EXTRA_L10N)) {
  translations[_lc] = deepMerge(translations[_lc] || {}, EXTRA_L10N[_lc]);
}

/** Patch minimi per lingue UE aggiuntive (fallback su IT per il resto). */
const EU_LANG_PATCHES = {
  pt: { gdpr: { ...GDPR_EN, download: "Transferir os meus dados (JSON)" }, ui: { language: "Idioma" } },
  nl: { gdpr: { ...GDPR_EN, title: "Privacy en gegevens" }, ui: { language: "Taal" } },
  pl: { gdpr: { ...GDPR_EN, title: "Prywatność i dane" }, ui: { language: "Język" } },
  hr: { gdpr: { ...GDPR_EN, title: "Privatnost i podaci" }, ui: { language: "Jezik" } },
  el: { gdpr: { ...GDPR_EN, title: "Απόρρητο και δεδομένα" }, ui: { language: "Γλώσσα" } },
  ro: { gdpr: { ...GDPR_EN, title: "Confidențialitate și date" }, ui: { language: "Limbă" } },
};

/**
 * Dizionario per lingua. IT = sorgente pura (nessun merge).
 * Lingue con dizionario completo (en/fr/de/es) = deepMerge(IT, locale).
 * Lingue UE senza dizionario completo = deepMerge(EN, patch):
 * meglio UI in inglese che in italiano per un visitatore polacco/portoghese.
 */
export function getTranslation(lang) {
  const code = isSupportedLang(lang) ? lang.split("-")[0].toLowerCase() : DEFAULT_LANG;
  const baseIT = translations[DEFAULT_LANG];
  if (code === DEFAULT_LANG) return baseIT;
  const full = translations[code];
  const patch = EU_LANG_PATCHES[code];
  if (full) return deepMerge(baseIT, full);
  const baseEN = translations.en || baseIT;
  if (patch) return deepMerge(baseEN, patch);
  return baseEN;
}

/**
 * Lingua iniziale piattaforma:
 * 1. Se l'utente ha già scelto una lingua, rispetta la sua scelta salvata.
 * 2. Altrimenti rileva la lingua del dispositivo (navigator.language).
 * 3. Default internazionale "en" per visitatori da aree non coperte
 *    (es. Cina, USA, Asia); italiani e lingue principali restano in lingua madre.
 */
export function detectInitialLang() {
  if (typeof navigator === "undefined") return "en";
  const raw = (
    navigator.language ||
    (navigator.languages && navigator.languages[0]) ||
    "it"
  ).toLowerCase();
  for (const code of ["it", "en", "fr", "de", "es"]) {
    if (raw.startsWith(code)) return code;
  }
  return "en";
}

export function resolveInitialLang({ explicit = false, saved = null } = {}) {
  if (explicit && saved) {
    const code = String(saved).split("-")[0].toLowerCase();
    return isSupportedLang(code) ? code : DEFAULT_LANG;
  }
  return detectInitialLang();
}
